import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import './App.css';
import { Card } from './components/ui/card';
import { Button } from './components/ui/button';
import { Input } from './components/ui/input';
import { ScrollArea } from './components/ui/scroll-area';
import { Badge } from './components/ui/badge';
import { Separator } from './components/ui/separator';
import { Sheet, SheetContent, SheetTrigger } from './components/ui/sheet';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from './components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './components/ui/tabs';
import { Label } from './components/ui/label';
import { Textarea } from './components/ui/textarea';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from './components/ui/alert-dialog';
import { Menu, Send, Loader2, MessageSquare, Trash2, User, LogOut, Settings, Plus, Edit, UserPlus, LogIn, Key, CreditCard, Check, X, DollarSign, Palette, Save, History, ChevronUp, ChevronDown, RefreshCw, Sun, Moon, Clipboard, ClipboardCheck, Download, BarChart2, Mail, Search, ChevronRight, Zap } from 'lucide-react';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import { Prism as SyntaxHighlighter } from 'react-syntax-highlighter';
import { vscDarkPlus } from 'react-syntax-highlighter/dist/esm/styles/prism';
import LandingPage from './LandingPage';

const AGENT_INFO = {
  general: {
    quickPrompts: ["My computer won't connect to WiFi","Computer is running very slow","How do I reset a Windows password?","My printer isn't detected"],
    icon: '🎯',
    title: 'General IT Support',
    description: `Your go-to assistant for any IT question that doesn't fit a specific category. Whether you're troubleshooting a connectivity issue, figuring out why an application won't open, or just need quick guidance on a tech problem, this is the right place to start.`,
    capabilities: 'Network issues · Software problems · Device setup · Password resets · General troubleshooting',
  },
  M365: {
    quickPrompts: ["How do I recover deleted emails in Outlook?","Set up MFA for a user in Microsoft 365","Teams meeting not showing in calendar","Fix OneDrive sync issues"],
    icon: '🏢',
    title: 'Microsoft 365 Support',
    description: `Get expert help with everything in the Microsoft 365 suite — from Outlook and Teams to SharePoint, OneDrive, and Exchange Online. Whether your email isn't syncing, a Teams meeting won't connect, or you need help managing licenses and permissions, this specialist has you covered.`,
    capabilities: 'Outlook & Exchange · Microsoft Teams · SharePoint & OneDrive · Licensing · Azure AD / Entra ID',
  },
  MacOS: {
    quickPrompts: ["Mac won't connect to company WiFi","How do I enroll Mac in Jamf MDM?","Reset macOS password without losing data","Mac running slow after update"],
    icon: '🍎',
    title: 'macOS Support',
    description: `Dedicated support for Apple Mac users — both personal and enterprise-managed Macs. Get help with system issues, app crashes, networking, iCloud, FileVault encryption, or MDM-enrolled devices. Works across all recent macOS versions on both Apple Silicon and Intel Macs.`,
    capabilities: 'System preferences & updates · App troubleshooting · Wi-Fi & VPN · Jamf / MDM · Apple Silicon',
  },
  Windows: {
    quickPrompts: ["Fix Windows Update stuck or failing","How to join a computer to the domain?","Blue screen of death (BSOD) troubleshooting","Reset Windows 11 without losing files"],
    icon: '🪟',
    title: 'Windows 11 Support',
    description: `Comprehensive support for Windows 11 in home, business, and enterprise environments. From slow performance and update failures to driver issues and domain policies, this specialist can walk you through step-by-step fixes using both the GUI and command-line tools.`,
    capabilities: 'Windows Update · Performance & BSODs · Group Policy · BitLocker · Active Directory login',
  },
  ActiveDirectory: {
    quickPrompts: ["Unlock a locked-out user account","Reset a user password in Active Directory","How do I create a Group Policy Object?","Fix account not found in AD"],
    icon: '🔐',
    title: 'Active Directory Support',
    description: `Specialized help for managing users, groups, and policies in on-premises Active Directory and Azure AD (Entra ID). Great for IT admins dealing with account lockouts, GPO issues, hybrid identity setups, or replication problems across domain controllers.`,
    capabilities: 'User & group management · Account lockouts · Group Policy · Azure AD Connect · LDAP & Kerberos',
  },
  DUO: {
    quickPrompts: ["User locked out of Duo MFA","How to add a new device to Duo","Duo push notifications not working","Set up Duo for a new employee"],
    icon: '🛡️',
    title: 'Duo Security (MFA) Support',
    description: `Your expert for everything related to Duo multi-factor authentication. Whether you're an end user locked out of Duo, needing to re-enroll a device, or an admin troubleshooting policy configurations and VPN integrations, this specialist knows Duo inside and out.`,
    capabilities: 'Duo Mobile setup · Push & passcode issues · Hardware tokens · Admin policies · VPN integration',
  },
  Azure: {
    quickPrompts: ["How to create a new Azure VM?","Fix Azure AD sign-in issues","Assign a static IP to an Azure VM","Reduce Azure monthly costs"],
    icon: '☁️',
    title: 'Azure Cloud Support',
    description: `Expert guidance on Microsoft Azure cloud services — from spinning up virtual machines and managing storage to configuring networking, security policies, and identity access. Ideal for cloud administrators, DevOps engineers, and IT pros working in Azure environments.`,
    capabilities: 'Virtual Machines · Networking & VPN · Azure AD · Storage & Blob · Cost management · Security',
  },
  PowerShell: {
    quickPrompts: ["Script to list all AD users and emails","Bulk reset passwords in Active Directory","Get all M365 licensed users via PowerShell","Schedule a PowerShell script as a task"],
    icon: '💻',
    title: 'PowerShell Scripting',
    description: `Get production-ready PowerShell scripts for Windows administration, Microsoft 365 management, Active Directory automation, and Azure tasks. Every script comes with error handling, comments, and best practices — ready to run or adapt for your environment.`,
    capabilities: 'AD & M365 automation · Bulk operations · Scheduled tasks · Azure Az module · Error handling',
  },
  Scripts: {
    quickPrompts: ["Bash script to monitor disk usage","Python script to parse log files","Automate SSH login and run commands","Schedule a backup script with cron"],
    icon: '📜',
    title: 'General Scripts & Automation',
    description: `Need to automate a repetitive IT task? This assistant can write and debug scripts in Bash, Python, PowerShell, or Batch for any platform. From log parsing and file operations to API calls and scheduled jobs, get clean, well-documented automation scripts fast.`,
    capabilities: 'Bash / Shell · Python automation · Batch files · API integrations · Log parsing · Cron jobs',
  },
  AWS: {
    quickPrompts: ["How to create an EC2 instance?","Fix S3 bucket access denied error","Set up auto-scaling for EC2","Analyze AWS cost spikes"],
    icon: '🚀',
    title: 'Amazon Web Services (AWS)',
    description: `Hands-on help with AWS cloud infrastructure and services. Whether you're troubleshooting EC2 instances, configuring IAM permissions, setting up S3 buckets, or diagnosing network issues in a VPC, this specialist provides clear steps and CLI commands for every solution.`,
    capabilities: 'EC2 & Auto Scaling · S3 & Storage · IAM & Security · VPC Networking · CloudWatch · Cost control',
  },
  Splunk: {
    quickPrompts: ["Search for failed login attempts in Splunk","Create a Splunk dashboard for server errors","Write SPL to find top 10 source IPs","Set up an alert for critical errors"],
    icon: '📊',
    title: 'Splunk Analytics',
    description: `Expert help with Splunk search, dashboards, and data analysis. Whether you're writing SPL queries to investigate security events, building operational dashboards, or troubleshooting data ingestion, this specialist can help you get the most out of your Splunk environment.`,
    capabilities: 'SPL search queries · Security analytics · Dashboard building · Alerts & reports · Data onboarding',
  },
  Grammar: {
    quickPrompts: ["Polish this email for a client","Fix grammar in my incident report","Make this message more professional","Improve clarity of this technical doc"],
    icon: '✍️',
    title: 'Grammar & Writing Assistant',
    description: `Paste any text — an email, report, ticket, or message — and get it back polished, professional, and error-free. This assistant fixes grammar, spelling, and punctuation while preserving your original meaning and adapting the tone to match your audience.`,
    capabilities: 'Grammar & spelling · Professional tone · Email polish · Technical writing · Clarity improvements',
  },
  Reports: {
    quickPrompts: ["Create an incident report template","Write a change request document","Generate a security findings report","Write a project status update"],
    icon: '📋',
    title: 'IT Reports Creator',
    description: `Generate structured, professional IT reports in seconds. Describe what happened or what you need to communicate, and this assistant will produce a complete report — incident summaries, change requests, project status updates, security findings, or executive briefs.`,
    capabilities: 'Incident reports · Change requests · Security findings · Executive summaries · Project status',
  },
};

const API_BASE_URL = process.env.REACT_APP_BACKEND_URL;

function App() {
  // Existing state
  const [messages, setMessages] = useState([]);
  const [inputMessage, setInputMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [currentAgent, setCurrentAgent] = useState('general');
  const [sessionId, setSessionId] = useState('');
  const [agents, setAgents] = useState([]);
  const [isMobile, setIsMobile] = useState(window.innerWidth < 768);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  
  // Authentication state
  const [user, setUser] = useState(null);
  const [authToken, setAuthToken] = useState(localStorage.getItem('auth_token'));
  const [showAuth, setShowAuth] = useState(false);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('login'); // 'login' or 'register'
  
  // Admin state
  const [showAdmin, setShowAdmin] = useState(false);
  const [categories, setCategories] = useState([]);
  const [editingCategory, setEditingCategory] = useState(null);
  const [newCategory, setNewCategory] = useState({ key: '', name: '', system_prompt: '', icon: '' });
  
  // Welcome message state
  const [welcomeMessage, setWelcomeMessage] = useState({ title: '', description: '' });
  const [showWelcomeEdit, setShowWelcomeEdit] = useState(false);
  const [editWelcomeData, setEditWelcomeData] = useState({ title: '', description: '', icon: '' });
  
  // Landing page content state
  const [landingContent, setLandingContent] = useState({ 
    title: '', 
    description: '', 
    icon: '', 
    icon_image_url: '', 
    content: '', 
    banner_image_url: '' 
  });
  const [showContentEdit, setShowContentEdit] = useState(false);
  const [editContentData, setEditContentData] = useState({ 
    title: '', 
    description: '', 
    icon: '', 
    icon_image_url: '', 
    content: '', 
    banner_image_url: '' 
  });
  
  // Admin settings state
  const [adminSettings, setAdminSettings] = useState({});
  const [showAdminSettings, setShowAdminSettings] = useState(false);
  const [editAdminSettings, setEditAdminSettings] = useState({});
  
  // Theme state
  const [currentTheme, setCurrentTheme] = useState(localStorage.getItem('theme') || 'dark');
  
  // Payment state
  const [showPayment, setShowPayment] = useState(false);
  const [paymentPackages, setPaymentPackages] = useState([]);
  const [isProcessingPayment, setIsProcessingPayment] = useState(false);
  const [messagesRemaining, setMessagesRemaining] = useState(null);
  
  // Password change state
  const [showPasswordChange, setShowPasswordChange] = useState(false);
  const [passwordData, setPasswordData] = useState({ current_password: '', new_password: '', confirm_password: '' });
  
  // Separate form states to prevent re-render issues
  const [loginForm, setLoginForm] = useState({ identifier: '', password: '' });
  const [registerForm, setRegisterForm] = useState({ username: '', email: '', password: '' });
  
  // File upload states
  const [uploadingFile, setUploadingFile] = useState(false);
  const [imageResizeOptions, setImageResizeOptions] = useState({ width: '', height: '' });
  
  // Chat history states
  const [chatSessions, setChatSessions] = useState([]);
  const [showChatHistory, setShowChatHistory] = useState(true);
  const [loadingChatHistory, setLoadingChatHistory] = useState(false);
  const [currentSessionId, setCurrentSessionId] = useState('');

  // Admin user management state
  const [adminUsers, setAdminUsers] = useState([]);
  const [showCreateUser, setShowCreateUser] = useState(false);
  const [newUserForm, setNewUserForm] = useState({ username: '', email: '', password: '', role: 'user' });
  const [showResetPassword, setShowResetPassword] = useState(false);
  const [resetPasswordTarget, setResetPasswordTarget] = useState(null);
  const [resetPasswordValue, setResetPasswordValue] = useState('');

  // New feature state
  const [copiedMessageId, setCopiedMessageId] = useState(null);
  const [chatSearchQuery, setChatSearchQuery] = useState('');
  const [adminStats, setAdminStats] = useState(null);
  const [loadingStats, setLoadingStats] = useState(false);
  const [forgotPasswordMode, setForgotPasswordMode] = useState(false);
  const [forgotPasswordEmail, setForgotPasswordEmail] = useState('');
  const [forgotPasswordSent, setForgotPasswordSent] = useState(false);
  const [resetPasswordToken, setResetPasswordToken] = useState(null);
  const [resetPasswordNewPass, setResetPasswordNewPass] = useState('');
  const [resetPasswordConfirm, setResetPasswordConfirm] = useState('');
  const [resetPasswordDone, setResetPasswordDone] = useState(false);
  const [emailVerifyToken, setEmailVerifyToken] = useState(null);
  const [emailVerifyStatus, setEmailVerifyStatus] = useState(null);
  const [resendingVerification, setResendingVerification] = useState(false);

  const messagesEndRef = useRef(null);

  // Initialize app
  useEffect(() => {
    if (authToken) {
      loadUserInfo();
    }
    loadLandingContent(); // Load for public landing page

    // Check URL for email verify or password reset tokens
    const urlParams = new URLSearchParams(window.location.search);
    const verifyToken = urlParams.get('verify_token');
    const resetToken = urlParams.get('reset_token');
    if (verifyToken) {
      setEmailVerifyToken(verifyToken);
      setEmailVerifyStatus('verifying');
      apiCall(`/auth/verify-email?token=${verifyToken}`).then(res => {
        setEmailVerifyStatus(res.ok ? 'success' : 'error');
        window.history.replaceState({}, document.title, window.location.pathname);
      }).catch(() => setEmailVerifyStatus('error'));
    }
    if (resetToken) {
      setResetPasswordToken(resetToken);
      setShowAuthModal(true);
      window.history.replaceState({}, document.title, window.location.pathname);
    }

    const handleResize = () => {
      setIsMobile(window.innerWidth < 768);
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, []);

  // Load agents and welcome message when user is authenticated
  useEffect(() => {
    if (user) {
      loadAgents();
      loadWelcomeMessage();
      loadPaymentPackages();
      loadLandingContent();
      if (user.role === 'admin') {
        loadAdminSettings();
      }
      const newSessionId = generateSessionId();
      setSessionId(newSessionId);
    }
  }, [user]);

  // Check for payment return
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    const sessionId = urlParams.get('session_id');
    if (sessionId && user) {
      checkPaymentStatus(sessionId);
    }
  }, [user]);

  // Auto scroll to bottom of messages
  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const generateSessionId = () => {
    return 'session_' + Math.random().toString(36).substr(2, 9) + '_' + Date.now();
  };

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  const apiCall = async (endpoint, options = {}) => {
    const headers = {
      'Content-Type': 'application/json',
      ...(authToken && { 'Authorization': `Bearer ${authToken}` }),
      ...options.headers
    };

    const response = await fetch(`${API_BASE_URL}/api${endpoint}`, {
      ...options,
      headers
    });

    if (response.status === 401) {
      logout();
      throw new Error('Authentication required');
    }

    return response;
  };

  const loadUserInfo = async () => {
    try {
      const response = await apiCall('/auth/me');
      if (response.ok) {
        const userData = await response.json();
        setUser(userData);
      } else {
        logout();
      }
    } catch (error) {
      console.error('Error loading user info:', error);
      logout();
    }
  };

  const loadAgents = async () => {
    try {
      const response = await apiCall('/agents');
      if (response.ok) {
        const agentsData = await response.json();
        setAgents(agentsData);
      }
    } catch (error) {
      console.error('Error loading agents:', error);
    }
  };

  const loadWelcomeMessage = async () => {
    try {
      const response = await apiCall('/welcome-message');
      if (response.ok) {
        const messageData = await response.json();
        setWelcomeMessage(messageData);
      }
    } catch (error) {
      console.error('Error loading welcome message:', error);
    }
  };

  const loadPaymentPackages = async () => {
    try {
      const response = await apiCall('/payment/packages');
      if (response.ok) {
        const packagesData = await response.json();
        setPaymentPackages(packagesData.packages || []);
      }
    } catch (error) {
      console.error('Error loading payment packages:', error);
    }
  };

  const loadLandingContent = async () => {
    try {
      const response = await apiCall('/landing-content');
      if (response.ok) {
        const contentData = await response.json();
        setLandingContent(contentData);
      }
    } catch (error) {
      console.error('Error loading landing content:', error);
    }
  };

  const loadAdminSettings = async () => {
    try {
      const response = await apiCall('/admin/settings');
      if (response.ok) {
        const settingsData = await response.json();
        setAdminSettings(settingsData);
      }
    } catch (error) {
      console.error('Error loading admin settings:', error);
    }
  };

  const loadCategories = async () => {
    try {
      const response = await apiCall('/admin/categories');
      if (response.ok) {
        const categoriesData = await response.json();
        setCategories(categoriesData);
      }
    } catch (error) {
      console.error('Error loading categories:', error);
    }
  };

  const loadAdminUsers = async () => {
    try {
      const response = await apiCall('/admin/users');
      if (response.ok) {
        const usersData = await response.json();
        setAdminUsers(usersData);
      }
    } catch (error) {
      console.error('Error loading admin users:', error);
    }
  };

  const copyMessage = (id, text) => {
    navigator.clipboard.writeText(text).then(() => {
      setCopiedMessageId(id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    }).catch(() => {
      const el = document.createElement('textarea');
      el.value = text;
      document.body.appendChild(el);
      el.select();
      document.execCommand('copy');
      document.body.removeChild(el);
      setCopiedMessageId(id);
      setTimeout(() => setCopiedMessageId(null), 2000);
    });
  };

  const exportChat = () => {
    if (messages.length === 0) { alert('No messages to export'); return; }
    const agentName = getCurrentAgentName();
    const lines = [`# Chat Export — ${agentName}`, `Date: ${new Date().toLocaleString()}`, `Session: ${currentSessionId || 'current'}`, '', '---', ''];
    messages.forEach(msg => {
      const time = new Date(msg.timestamp).toLocaleTimeString();
      const role = msg.sender === 'user' ? '👤 You' : '🤖 AI';
      lines.push(`**${role}** [${time}]`);
      lines.push(msg.message);
      lines.push('');
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/markdown' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `chat-${agentName.replace(/\s+/g, '-').toLowerCase()}-${Date.now()}.md`;
    a.click();
    URL.revokeObjectURL(url);
  };

  const loadAdminStats = async () => {
    setLoadingStats(true);
    try {
      const response = await apiCall('/admin/stats');
      if (response.ok) {
        const data = await response.json();
        setAdminStats(data);
      }
    } catch (error) {
      console.error('Error loading stats:', error);
    } finally {
      setLoadingStats(false);
    }
  };

  const handleForgotPassword = async (e) => {
    e.preventDefault();
    setIsLoading(true);
    try {
      const response = await apiCall('/auth/forgot-password', {
        method: 'POST',
        body: JSON.stringify({ email: forgotPasswordEmail })
      });
      if (response.ok) {
        setForgotPasswordSent(true);
      }
    } catch (error) {
      console.error('Forgot password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResetPassword = async (e) => {
    e.preventDefault();
    if (resetPasswordNewPass !== resetPasswordConfirm) { alert('Passwords do not match'); return; }
    if (resetPasswordNewPass.length < 6) { alert('Password must be at least 6 characters'); return; }
    setIsLoading(true);
    try {
      const response = await apiCall('/auth/reset-password', {
        method: 'POST',
        body: JSON.stringify({ token: resetPasswordToken, new_password: resetPasswordNewPass })
      });
      if (response.ok) {
        setResetPasswordDone(true);
        setResetPasswordToken(null);
        setTimeout(() => { setResetPasswordDone(false); setShowAuthModal(true); setAuthMode('login'); }, 3000);
      } else {
        const err = await response.json();
        alert(err.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Reset password error:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleResendVerification = async () => {
    setResendingVerification(true);
    try {
      const response = await apiCall('/auth/resend-verification', { method: 'POST' });
      if (response.ok) {
        alert('Verification email sent! Please check your inbox.');
      }
    } catch (error) {
      console.error('Resend verification error:', error);
    } finally {
      setResendingVerification(false);
    }
  };

  const createAdminUser = async (e) => {
    e.preventDefault();
    try {
      const response = await apiCall('/admin/users', {
        method: 'POST',
        body: JSON.stringify(newUserForm)
      });
      if (response.ok) {
        await loadAdminUsers();
        setShowCreateUser(false);
        setNewUserForm({ username: '', email: '', password: '', role: 'user' });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create user');
      }
    } catch (error) {
      console.error('Error creating user:', error);
      alert('Failed to create user');
    }
  };

  const toggleUserActive = async (userId, currentActive) => {
    try {
      const response = await apiCall(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ is_active: !currentActive })
      });
      if (response.ok) {
        await loadAdminUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update user');
      }
    } catch (error) {
      console.error('Error updating user:', error);
      alert('Failed to update user');
    }
  };

  const changeUserRole = async (userId, newRole) => {
    try {
      const response = await apiCall(`/admin/users/${userId}`, {
        method: 'PUT',
        body: JSON.stringify({ role: newRole })
      });
      if (response.ok) {
        await loadAdminUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update user role');
      }
    } catch (error) {
      console.error('Error updating user role:', error);
      alert('Failed to update user role');
    }
  };

  const adminResetPassword = async (e) => {
    e.preventDefault();
    if (!resetPasswordValue.trim()) { alert('Please enter a new password'); return; }
    try {
      const response = await apiCall(`/admin/users/${resetPasswordTarget.id}`, {
        method: 'PUT',
        body: JSON.stringify({ password: resetPasswordValue })
      });
      if (response.ok) {
        alert(`Password reset successfully for ${resetPasswordTarget.username}`);
        setShowResetPassword(false);
        setResetPasswordTarget(null);
        setResetPasswordValue('');
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to reset password');
      }
    } catch (error) {
      console.error('Error resetting password:', error);
      alert('Failed to reset password');
    }
  };

  const deleteAdminUser = async (userId, username) => {
    if (!window.confirm(`Are you sure you want to permanently delete user "${username}"? This cannot be undone.`)) return;
    try {
      const response = await apiCall(`/admin/users/${userId}`, { method: 'DELETE' });
      if (response.ok) {
        await loadAdminUsers();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete user');
      }
    } catch (error) {
      console.error('Error deleting user:', error);
      alert('Failed to delete user');
    }
  };

  const handleAuth = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const endpoint = authMode === 'login' ? '/auth/login' : '/auth/register';
      const payload = authMode === 'login' 
        ? { identifier: loginForm.identifier, password: loginForm.password }
        : { username: registerForm.username, email: registerForm.email, password: registerForm.password };

      const response = await apiCall(endpoint, {
        method: 'POST',
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        const data = await response.json();
        setAuthToken(data.access_token);
        localStorage.setItem('auth_token', data.access_token);
        setUser({ 
          id: data.user_id, 
          username: data.username, 
          role: data.role,
          has_paid: data.has_paid,
          messages_used: data.messages_used 
        });
        setShowAuth(false);
        setLoginForm({ identifier: '', password: '' });
        setRegisterForm({ username: '', email: '', password: '' });
      } else {
        const error = await response.json();
        alert(error.detail || 'Authentication failed');
      }
    } catch (error) {
      console.error('Auth error:', error);
      alert('Authentication failed');
    } finally {
      setIsLoading(false);
    }
  };

  const logout = () => {
    setUser(null);
    setAuthToken(null);
    localStorage.removeItem('auth_token');
    setMessages([]);
    setSessionId('');
    // Clear URL parameters
    window.history.replaceState({}, document.title, window.location.pathname);
  };

  const sendMessage = async () => {
    if (!inputMessage.trim() || isLoading || !user) return;

    const userMessage = {
      id: Date.now(),
      message: inputMessage,
      sender: 'user',
      timestamp: new Date().toISOString()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputMessage('');
    setIsLoading(true);

    try {
      const response = await apiCall('/chat', {
        method: 'POST',
        body: JSON.stringify({
          message: inputMessage,
          agent_type: currentAgent,
          session_id: sessionId
        })
      });

      if (response.status === 402) {
        // Payment required
        const errorData = await response.json();
        setShowPayment(true);
        throw new Error(errorData.detail || 'Payment required for continued access');
      }

      if (!response.ok) {
        throw new Error('Failed to get response');
      }

      const data = await response.json();

      const aiMessage = {
        id: Date.now() + 1,
        message: data.response,
        sender: 'ai',
        agent_type: data.agent_type,
        timestamp: new Date().toISOString()
      };

      setMessages(prev => [...prev, aiMessage]);
      
      // Update messages remaining count
      if (data.messages_remaining !== null) {
        setMessagesRemaining(data.messages_remaining);
        if (data.messages_remaining === 0) {
          setShowPayment(true);
        }
      }
      
      // Update current session ID and refresh chat history
      if (data.session_id) {
        setCurrentSessionId(data.session_id);
      }
      
      // Refresh chat sessions list
      loadChatSessions();
      
    } catch (error) {
      console.error('Error sending message:', error);
      const errorMessage = {
        id: Date.now() + 1,
        message: error.message || 'Sorry, there was an error processing your request. Please try again.',
        sender: 'ai',
        isError: true,
        timestamp: new Date().toISOString()
      };
      setMessages(prev => [...prev, errorMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const clearChat = () => {
    setMessages([]);
    const newSessionId = generateSessionId();
    setSessionId(newSessionId);
    setCurrentSessionId('');
    // Refresh chat history to show updated sessions
    if (user) {
      loadChatSessions();
    }
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendMessage();
    }
  };

  const selectAgent = (agentKey) => {
    setCurrentAgent(agentKey);
    setIsSheetOpen(false);
  };

  const getCurrentAgentName = () => {
    const agent = agents.find(a => a.key === currentAgent);
    return agent ? agent.name : 'General Support';
  };

  const saveCategory = async () => {
    try {
      const endpoint = editingCategory 
        ? `/admin/categories/${editingCategory.key}`
        : '/admin/categories';
      
      const method = editingCategory ? 'PUT' : 'POST';
      const payload = editingCategory 
        ? { ...newCategory }
        : newCategory;

      const response = await apiCall(endpoint, {
        method,
        body: JSON.stringify(payload)
      });

      if (response.ok) {
        loadCategories();
        loadAgents();
        setNewCategory({ key: '', name: '', system_prompt: '', icon: '' });
        setEditingCategory(null);
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to save category');
      }
    } catch (error) {
      console.error('Error saving category:', error);
      alert('Failed to save category');
    }
  };

  const deleteCategory = async (categoryKey) => {
    try {
      const response = await apiCall(`/admin/categories/${categoryKey}`, {
        method: 'DELETE'
      });

      if (response.ok) {
        loadCategories();
        loadAgents();
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to delete category');
      }
    } catch (error) {
      console.error('Error deleting category:', error);
      alert('Failed to delete category');
    }
  };

  const startEditCategory = (category) => {
    setEditingCategory(category);
    setNewCategory({
      key: category.key,
      name: category.name,
      system_prompt: category.system_prompt,
      icon: category.icon
    });
  };

  const saveWelcomeMessage = async () => {
    try {
      const response = await apiCall('/admin/welcome-message', {
        method: 'PUT',
        body: JSON.stringify(editWelcomeData)
      });

      if (response.ok) {
        const updatedMessage = await response.json();
        setWelcomeMessage(updatedMessage);
        setShowWelcomeEdit(false);
        setEditWelcomeData({ title: '', description: '', icon: '', icon_image_url: '' });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update welcome message');
      }
    } catch (error) {
      console.error('Error updating welcome message:', error);
      alert('Failed to update welcome message');
    }
  };

  const initiatePayment = async (packageId) => {
    setIsProcessingPayment(true);
    
    try {
      const originUrl = window.location.origin;
      const response = await apiCall('/payment/checkout', {
        method: 'POST',
        body: JSON.stringify({
          package_id: packageId,
          origin_url: originUrl
        })
      });

      if (response.ok) {
        const data = await response.json();
        // Redirect to Stripe Checkout
        window.location.href = data.url;
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to create checkout session');
      }
    } catch (error) {
      console.error('Payment error:', error);
      alert('Failed to process payment');
    } finally {
      setIsProcessingPayment(false);
    }
  };

  const checkPaymentStatus = async (sessionId, attempts = 0) => {
    const maxAttempts = 10;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      console.log('Payment status check timed out');
      return;
    }

    try {
      const response = await apiCall(`/payment/status/${sessionId}`);
      if (response.ok) {
        const data = await response.json();
        
        if (data.payment_status === 'paid') {
          // Reload user info to get updated payment status
          await loadUserInfo();
          setShowPayment(false);
          alert('Payment successful! You now have unlimited access.');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        } else if (data.status === 'expired') {
          alert('Payment session expired. Please try again.');
          // Clear URL parameters
          window.history.replaceState({}, document.title, window.location.pathname);
          return;
        }

        // If payment is still pending, continue polling
        setTimeout(() => checkPaymentStatus(sessionId, attempts + 1), pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
    }
  };

  const saveLandingContent = async () => {
    try {
      const response = await apiCall('/admin/landing-content', {
        method: 'PUT',
        body: JSON.stringify(editContentData)
      });

      if (response.ok) {
        const updatedContent = await response.json();
        setLandingContent(updatedContent);
        setShowContentEdit(false);
        setEditContentData({ 
          title: '', 
          description: '', 
          icon: '', 
          icon_image_url: '', 
          content: '', 
          banner_image_url: '' 
        });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update landing content');
      }
    } catch (error) {
      console.error('Error updating landing content:', error);
      alert('Failed to update landing content');
    }
  };

  const saveAdminSettings = async () => {
    try {
      const response = await apiCall('/admin/settings', {
        method: 'PUT',
        body: JSON.stringify(editAdminSettings)
      });

      if (response.ok) {
        const updatedSettings = await response.json();
        setAdminSettings(updatedSettings);
        setShowAdminSettings(false);
        setEditAdminSettings({});
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to update admin settings');
      }
    } catch (error) {
      console.error('Error updating admin settings:', error);
      alert('Failed to update admin settings');
    }
  };

  const changePassword = async (e) => {
    e.preventDefault();
    
    if (passwordData.new_password !== passwordData.confirm_password) {
      alert('New passwords do not match');
      return;
    }

    try {
      const response = await apiCall('/auth/change-password', {
        method: 'POST',
        body: JSON.stringify({
          current_password: passwordData.current_password,
          new_password: passwordData.new_password
        })
      });

      if (response.ok) {
        alert('Password changed successfully');
        setShowPasswordChange(false);
        setPasswordData({ current_password: '', new_password: '', confirm_password: '' });
      } else {
        const error = await response.json();
        alert(error.detail || 'Failed to change password');
      }
    } catch (error) {
      console.error('Error changing password:', error);
      alert('Failed to change password');
    }
  };

  const changeTheme = (theme) => {
    setCurrentTheme(theme);
    localStorage.setItem('theme', theme);
    if (theme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  };

  const toggleTheme = () => {
    changeTheme(currentTheme === 'dark' ? 'light' : 'dark');
  };

  // Apply theme before first paint to prevent flash
  useLayoutEffect(() => {
    if (currentTheme === 'dark') {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [currentTheme]);

  const AgentSidebar = () => (
    <div className="h-full bg-card border-r border-border flex flex-col overflow-hidden">
      <div className="flex-1 overflow-y-auto min-h-0 p-6">
        <div className="flex items-center space-x-2 mb-6">
          <MessageSquare className="h-6 w-6 text-blue-600" />
          <h2 className="text-xl font-bold text-foreground">Tech Assistant</h2>
        </div>
        
        {/* User info moved to header */}

        {/* Theme Selector moved to Admin Panel */}

        {/* Trial/Payment notification - only for regular users */}
        {user && !user.has_paid && user.role !== 'admin' && (
          <Card className="p-3 mb-4 bg-orange-50 border-orange-200">
            <div className="text-sm text-orange-800">
              <p className="font-medium">Free Trial</p>
              <p className="text-xs mt-1">
                {messagesRemaining !== null ? `${messagesRemaining} messages remaining` : `${5 - (user.messages_used || 0)} messages left`}
              </p>
              <Button 
                size="sm" 
                className="mt-2 w-full bg-orange-600 hover:bg-orange-700"
                onClick={() => setShowPayment(true)}
                data-testid="upgrade-btn"
              >
                <CreditCard className="h-3 w-3 mr-1" />
                Upgrade ($20)
              </Button>
            </div>
          </Card>
        )}
        
        <div className="space-y-2">
          <Button
            onClick={() => selectAgent('general')}
            variant={currentAgent === 'general' ? 'default' : 'ghost'}
            className="w-full justify-start text-left"
            data-testid="general-agent-btn"
          >
            {currentTheme !== 'terminal' && <span className="mr-2">🎯</span>}
            General Support
          </Button>
          
          {agents.map((agent) => (
            <Button
              key={agent.key}
              onClick={() => selectAgent(agent.key)}
              variant={currentAgent === agent.key ? 'default' : 'ghost'}
              className="w-full justify-start text-left"
              data-testid={`${agent.key.toLowerCase()}-agent-btn`}
            >
              {currentTheme !== 'terminal' && <span className="mr-2">{agent.icon}</span>}
              {agent.name}
            </Button>
          ))}
        </div>
        
        {/* Chat History Section */}
        {user && (
          <div className="mt-6 border-t pt-4">
            <div className="flex items-center justify-between mb-3">
              <h3 className="text-sm font-semibold text-foreground flex items-center">
                <History className="h-4 w-4 mr-2" />
                Chat History
              </h3>
              <Button
                size="sm"
                variant="ghost"
                onClick={() => setShowChatHistory(!showChatHistory)}
                className="p-1 h-6 w-6"
                title={showChatHistory ? "Hide Chat History" : "Show Chat History"}
                data-testid="toggle-chat-history"
              >
                {showChatHistory ? (
                  <ChevronUp className="h-3 w-3" />
                ) : (
                  <ChevronDown className="h-3 w-3" />
                )}
              </Button>
            </div>
            
            {showChatHistory && (
              <div className="mb-2">
                <div className="relative">
                  <Search className="absolute left-2 top-1/2 -translate-y-1/2 h-3 w-3 text-muted-foreground" />
                  <input
                    type="text"
                    placeholder="Search chats..."
                    value={chatSearchQuery}
                    onChange={(e) => setChatSearchQuery(e.target.value)}
                    className="w-full pl-7 pr-2 py-1.5 text-xs bg-muted border border-border rounded-md focus:outline-none focus:ring-1 focus:ring-ring text-foreground placeholder:text-muted-foreground"
                  />
                </div>
              </div>
            )}

            {showChatHistory && (
              <div className="space-y-1 max-h-64 overflow-y-auto">
                {loadingChatHistory ? (
                  <div className="text-xs text-muted-foreground text-center py-4">
                    Loading chat history...
                  </div>
                ) : chatSessions.length === 0 ? (
                  <div className="text-xs text-muted-foreground text-center py-4">
                    No previous chats
                  </div>
                ) : (
                  chatSessions.filter(s => !chatSearchQuery || (s.first_user_message || '').toLowerCase().includes(chatSearchQuery.toLowerCase())).map((session) => (
                    <div
                      key={session.session_id}
                      className={`group flex items-center justify-between p-2 rounded text-xs hover:bg-muted cursor-pointer transition-colors ${
                        currentSessionId === session.session_id
                          ? 'bg-primary/10 border border-primary/30'
                          : 'border border-transparent'
                      }`}
                      onClick={() => loadChatHistory(session.session_id)}
                      data-testid={`chat-session-${session.session_id}`}
                    >
                      <div className="flex-1 min-w-0">
                        <div className="font-medium text-foreground truncate">
                          {formatSessionTitle(session)}
                        </div>
                        <div className="text-muted-foreground truncate">
                          {formatLastMessage(session.last_message)}
                        </div>
                      </div>
                      <Button
                        size="sm"
                        variant="ghost"
                        onClick={(e) => {
                          e.stopPropagation();
                          deleteChatSession(session.session_id);
                        }}
                        className="opacity-0 group-hover:opacity-100 p-1 h-6 w-6 ml-2"
                        title="Delete Chat"
                        data-testid={`delete-session-${session.session_id}`}
                      >
                        <Trash2 className="h-3 w-3 text-red-500" />
                      </Button>
                    </div>
                  ))
                )}
                
                {/* Refresh button */}
                <Button
                  size="sm"
                  variant="outline"
                  onClick={loadChatSessions}
                  className="w-full mt-2 text-xs"
                  disabled={loadingChatHistory}
                  data-testid="refresh-chat-history"
                >
                  <RefreshCw className={`h-3 w-3 mr-1 ${loadingChatHistory ? 'animate-spin' : ''}`} />
                  Refresh
                </Button>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Bottom branding */}
      <div className="flex-shrink-0 border-t border-border px-6 py-3 bg-card">
        <p className="text-xs text-muted-foreground">Developed by</p>
        <p className="text-xs font-semibold text-primary">RG Integrated Solutions</p>
      </div>
    </div>
  );

  // Removed complex handlers - using simple direct state updates

  // Password Change Dialog
  const PasswordChangeDialog = () => {
    const handlePasswordInputChange = (field, value) => {
      setPasswordData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    return (
      <Dialog open={showPasswordChange} onOpenChange={setShowPasswordChange}>
        <DialogContent className="sm:max-w-md" data-testid="password-change-dialog">
          <DialogHeader>
            <DialogTitle>Change Password</DialogTitle>
          </DialogHeader>
          <form onSubmit={changePassword} className="space-y-4">
            <div>
              <Label htmlFor="current-password-input">Current Password</Label>
              <Input
                id="current-password-input"
                name="current-password"
                type="password"
                value={passwordData.current_password}
                onChange={(e) => handlePasswordInputChange('current_password', e.target.value)}
                placeholder="Enter current password"
                required
                autoComplete="current-password"
                data-testid="current-password"
              />
            </div>
            <div>
              <Label htmlFor="new-password-input">New Password</Label>
              <Input
                id="new-password-input"
                name="new-password"
                type="password"
                value={passwordData.new_password}
                onChange={(e) => handlePasswordInputChange('new_password', e.target.value)}
                placeholder="Enter new password"
                required
                autoComplete="new-password"
                data-testid="new-password"
              />
            </div>
            <div>
              <Label htmlFor="confirm-password-input">Confirm New Password</Label>
              <Input
                id="confirm-password-input"
                name="confirm-password"
                type="password"
                value={passwordData.confirm_password}
                onChange={(e) => handlePasswordInputChange('confirm_password', e.target.value)}
                placeholder="Confirm new password"
                required
                autoComplete="new-password"
                data-testid="confirm-password"
              />
            </div>
            <div className="flex space-x-2">
              <Button type="submit" className="flex-1" data-testid="change-password-submit">
                <Key className="h-4 w-4 mr-2" />
                Change Password
              </Button>
              <Button 
                type="button" 
                variant="outline" 
                onClick={() => setShowPasswordChange(false)}
                data-testid="change-password-cancel"
              >
                Cancel
              </Button>
            </div>
          </form>
        </DialogContent>
      </Dialog>
    );
  };

  // Payment Dialog
  const PaymentDialog = () => (
    <Dialog open={showPayment} onOpenChange={setShowPayment}>
      <DialogContent className="sm:max-w-lg" data-testid="payment-dialog">
        <DialogHeader>
          <DialogTitle>Upgrade to Full Access</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="text-center">
            <div className="text-6xl mb-4">💳</div>
            <h3 className="text-xl font-semibold mb-2">Get Unlimited Access</h3>
            <p className="text-muted-foreground">
              Continue your helpdesk conversations without limits
            </p>
          </div>

          {paymentPackages.map((pkg) => (
            <Card key={pkg.id} className="p-6">
              <div className="flex items-center justify-between">
                <div>
                  <h4 className="text-lg font-semibold">{pkg.name}</h4>
                  <p className="text-sm text-muted-foreground mt-1">{pkg.description}</p>
                </div>
                <div className="text-right">
                  <div className="text-2xl font-bold">${pkg.price}</div>
                  <div className="text-sm text-muted-foreground">{pkg.currency}</div>
                </div>
              </div>
              <div className="mt-4 space-y-2">
                <Button 
                  className="w-full bg-blue-600 hover:bg-blue-700" 
                  onClick={() => initiatePayment(pkg.id)}
                  disabled={isProcessingPayment}
                  data-testid="credit-card-payment"
                >
                  {isProcessingPayment ? (
                    <Loader2 className="h-4 w-4 animate-spin mr-2" />
                  ) : (
                    <CreditCard className="h-4 w-4 mr-2" />
                  )}
                  Pay with Credit Card
                </Button>
                <Button 
                  className="w-full bg-black hover:bg-gray-800 text-white" 
                  onClick={() => initiatePayment(pkg.id)}
                  disabled={isProcessingPayment}
                  data-testid="google-pay-payment"
                >
                  <DollarSign className="h-4 w-4 mr-2" />
                  Google Pay & PayPal
                </Button>
              </div>
            </Card>
          ))}

          <div className="text-center text-sm text-muted-foreground">
            Secure payment processed by Stripe
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  // File upload functions
  const uploadImage = async (file, resizeWidth = null, resizeHeight = null) => {
    const formData = new FormData();
    formData.append('file', file);
    if (resizeWidth) formData.append('max_width', resizeWidth);
    if (resizeHeight) formData.append('max_height', resizeHeight);

    const response = await fetch(`${API_BASE_URL}/api/upload/image`, {
      method: 'POST',
      headers: {
        'Authorization': `Bearer ${authToken}`
      },
      body: formData
    });

    if (!response.ok) {
      const error = await response.json();
      throw new Error(error.detail || 'Upload failed');
    }

    return await response.json();
  };

  const handleFileUpload = async (file, onSuccess, resizeWidth = null, resizeHeight = null) => {
    if (!file) return;

    // Validate file size (2MB)
    if (file.size > 2 * 1024 * 1024) {
      alert('File size must be less than 2MB');
      return;
    }

    // Validate file type
    const allowedTypes = ['image/jpeg', 'image/png', 'image/gif', 'image/webp'];
    if (!allowedTypes.includes(file.type)) {
      alert('Only JPEG, PNG, GIF, and WebP files are allowed');
      return;
    }

    try {
      setUploadingFile(true);
      const result = await uploadImage(file, resizeWidth, resizeHeight);
      onSuccess(result.url);
      alert('Image uploaded successfully!');
    } catch (error) {
      console.error('Upload error:', error);
      alert(`Upload failed: ${error.message}`);
    } finally {
      setUploadingFile(false);
    }
  };

  // Chat History functions
  const loadChatSessions = async () => {
    if (!user) return;
    
    try {
      setLoadingChatHistory(true);
      const response = await apiCall('/chat/sessions');
      if (response.ok) {
        const sessions = await response.json();
        setChatSessions(sessions);
      }
    } catch (error) {
      console.error('Error loading chat sessions:', error);
    } finally {
      setLoadingChatHistory(false);
    }
  };

  const loadChatHistory = async (sessionId) => {
    try {
      const response = await apiCall(`/chat/history/${sessionId}`);
      if (response.ok) {
        const history = await response.json();
        setMessages(history);
        setCurrentSessionId(sessionId);
      }
    } catch (error) {
      console.error('Error loading chat history:', error);
      alert('Failed to load chat history');
    }
  };

  const deleteChatSession = async (sessionId) => {
    if (!confirm('Are you sure you want to delete this chat session?')) return;
    
    try {
      const response = await apiCall(`/chat/history/${sessionId}`, { method: 'DELETE' });
      if (response.ok) {
        await loadChatSessions();
        // If we deleted the current session, clear the chat
        if (sessionId === currentSessionId) {
          clearChat();
        }
      }
    } catch (error) {
      console.error('Error deleting chat session:', error);
      alert('Failed to delete chat session');
    }
  };

  const formatSessionTitle = (session) => {
    if (session.message_count === 0) return 'Empty Chat';
    const msg = session.first_user_message || '';
    if (msg.trim()) {
      return msg.length > 45 ? msg.substring(0, 45).trim() + '…' : msg.trim();
    }
    return 'New Chat';
  };

  const formatLastMessage = (timestamp) => {
    const date = new Date(timestamp);
    const now = new Date();
    const diffTime = Math.abs(now - date);
    const diffDays = Math.floor(diffTime / (1000 * 60 * 60 * 24));
    
    if (diffDays === 0) {
      return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
    } else if (diffDays === 1) {
      return 'Yesterday';
    } else if (diffDays < 7) {
      return `${diffDays} days ago`;
    } else {
      return date.toLocaleDateString();
    }
  };

  // Load chat sessions when user logs in
  useEffect(() => {
    if (user) {
      loadChatSessions();
    }
  }, [user]);

  // Welcome Message Edit Dialog
  const WelcomeEditDialog = () => {
    const handleWelcomeInputChange = (field, value) => {
      setEditWelcomeData(prev => ({
        ...prev,
        [field]: value
      }));
    };

    const handleIconImageUpload = (event) => {
      const file = event.target.files[0];
      const width = imageResizeOptions.width ? parseInt(imageResizeOptions.width) : null;
      const height = imageResizeOptions.height ? parseInt(imageResizeOptions.height) : null;
      
      handleFileUpload(file, (url) => {
        handleWelcomeInputChange('icon_image_url', url);
      }, width, height);
    };

    return (
      <Dialog open={showWelcomeEdit} onOpenChange={setShowWelcomeEdit}>
        <DialogContent className="sm:max-w-2xl" data-testid="welcome-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Welcome Message</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="welcome-icon-input">Icon (Emoji)</Label>
                <Input
                  id="welcome-icon-input"
                  name="icon"
                  value={editWelcomeData.icon || ''}
                  onChange={(e) => handleWelcomeInputChange('icon', e.target.value)}
                  placeholder="👋"
                  data-testid="welcome-icon-input"
                />
              </div>
              <div>
                <Label>Or Upload Icon Image</Label>
                <div className="space-y-2">
                  <Input
                    type="file"
                    accept="image/*"
                    onChange={handleIconImageUpload}
                    disabled={uploadingFile}
                    data-testid="icon-image-upload"
                  />
                  {editWelcomeData.icon_image_url && (
                    <div className="flex items-center space-x-2">
                      <img 
                        src={`${API_BASE_URL}${editWelcomeData.icon_image_url}`} 
                        alt="Icon preview" 
                        className="w-8 h-8 object-cover rounded"
                      />
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => handleWelcomeInputChange('icon_image_url', '')}
                      >
                        <X className="h-3 w-3" />
                      </Button>
                    </div>
                  )}
                </div>
              </div>
            </div>
            
            {/* Image Resize Options */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2">Image Resize Options (Optional)</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="resize-width">Max Width (px)</Label>
                  <Input
                    id="resize-width"
                    type="number"
                    placeholder="e.g., 64"
                    value={imageResizeOptions.width}
                    onChange={(e) => setImageResizeOptions(prev => ({ ...prev, width: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="resize-height">Max Height (px)</Label>
                  <Input
                    id="resize-height"
                    type="number"
                    placeholder="e.g., 64"
                    value={imageResizeOptions.height}
                    onChange={(e) => setImageResizeOptions(prev => ({ ...prev, height: e.target.value }))}
                  />
                </div>
              </div>
            </div>
            <div>
              <Label htmlFor="welcome-title-input">Title</Label>
              <Input
                id="welcome-title-input"
                name="title"
                value={editWelcomeData.title}
                onChange={(e) => handleWelcomeInputChange('title', e.target.value)}
                placeholder="Welcome message title"
                data-testid="welcome-title-input"
              />
            </div>
            <div>
              <Label htmlFor="welcome-description-input">Description</Label>
              <Textarea
                id="welcome-description-input"
                name="description"
                value={editWelcomeData.description}
                onChange={(e) => handleWelcomeInputChange('description', e.target.value)}
                placeholder="Welcome message description"
                rows={4}
                data-testid="welcome-description-input"
              />
            </div>
            <div className="flex space-x-2">
              <Button onClick={saveWelcomeMessage} data-testid="save-welcome-message">
                <Check className="h-4 w-4 mr-2" />
                Save Changes
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowWelcomeEdit(false)}
                data-testid="cancel-welcome-edit"
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Admin Panel Dialog
  const AdminPanel = () => (
    <Dialog open={showAdmin} onOpenChange={setShowAdmin}>
      <DialogContent className="w-full max-w-6xl max-h-[90vh] overflow-y-auto" data-testid="admin-panel">
        <DialogHeader>
          <DialogTitle>Admin Panel - Manage System</DialogTitle>
        </DialogHeader>

        <Tabs defaultValue="categories" className="w-full">
          <TabsList className="flex overflow-x-auto w-full gap-0.5 h-auto flex-wrap sm:flex-nowrap">
            <TabsTrigger value="categories" className="text-xs sm:text-sm whitespace-nowrap">Categories</TabsTrigger>
            <TabsTrigger value="users" className="text-xs sm:text-sm whitespace-nowrap">Users</TabsTrigger>
            <TabsTrigger value="welcome" className="text-xs sm:text-sm whitespace-nowrap">Welcome</TabsTrigger>
            <TabsTrigger value="content" className="text-xs sm:text-sm whitespace-nowrap">Landing</TabsTrigger>
            <TabsTrigger value="theme" className="text-xs sm:text-sm whitespace-nowrap">Theme</TabsTrigger>
            <TabsTrigger value="settings" className="text-xs sm:text-sm whitespace-nowrap">Settings</TabsTrigger>
            <TabsTrigger value="stats" className="text-xs sm:text-sm whitespace-nowrap">Stats</TabsTrigger>
          </TabsList>
          
          <TabsContent value="categories" className="space-y-6">
            {/* Add/Edit Category Form */}
            <Card className="p-4">
              <h3 className="text-lg font-semibold mb-4">
                {editingCategory ? 'Edit Category' : 'Add New Category'}
              </h3>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                <div>
                  <Label htmlFor="cat-key">Key</Label>
                  <Input
                    id="cat-key"
                    value={newCategory.key}
                    onChange={(e) => setNewCategory({...newCategory, key: e.target.value})}
                    placeholder="e.g., Windows"
                    disabled={!!editingCategory}
                    data-testid="category-key-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cat-name">Name</Label>
                  <Input
                    id="cat-name"
                    value={newCategory.name}
                    onChange={(e) => setNewCategory({...newCategory, name: e.target.value})}
                    placeholder="e.g., Windows 11 Support"
                    data-testid="category-name-input"
                  />
                </div>
                <div>
                  <Label htmlFor="cat-icon">Icon (emoji)</Label>
                  <Input
                    id="cat-icon"
                    value={newCategory.icon}
                    onChange={(e) => setNewCategory({...newCategory, icon: e.target.value})}
                    placeholder="🪟"
                    data-testid="category-icon-input"
                  />
                </div>
                <div className="col-span-2">
                  <Label htmlFor="cat-prompt">System Prompt</Label>
                  <Textarea
                    id="cat-prompt"
                    value={newCategory.system_prompt}
                    onChange={(e) => setNewCategory({...newCategory, system_prompt: e.target.value})}
                    placeholder="You are a Windows 11 support specialist..."
                    rows={4}
                    data-testid="category-prompt-input"
                  />
                </div>
              </div>
              <div className="flex space-x-2 mt-4">
                <Button onClick={saveCategory} data-testid="save-category-btn">
                  {editingCategory ? 'Update Category' : 'Add Category'}
                </Button>
                {editingCategory && (
                  <Button 
                    variant="outline" 
                    onClick={() => {
                      setEditingCategory(null);
                      setNewCategory({ key: '', name: '', system_prompt: '', icon: '' });
                    }}
                    data-testid="cancel-edit-btn"
                  >
                    Cancel
                  </Button>
                )}
              </div>
            </Card>

            {/* Existing Categories */}
            <div>
              <h3 className="text-lg font-semibold mb-4">Existing Categories</h3>
              <div className="space-y-2">
                {categories.map((category) => (
                  <Card key={category.key} className="p-4">
                    <div className="flex items-center justify-between">
                      <div className="flex items-center space-x-3">
                        <span className="text-2xl">{category.icon}</span>
                        <div>
                          <h4 className="font-medium">{category.name}</h4>
                          <p className="text-sm text-muted-foreground">Key: {category.key}</p>
                          <p className="text-sm text-muted-foreground mt-1 line-clamp-2">
                            {category.system_prompt.substring(0, 100)}...
                          </p>
                        </div>
                      </div>
                      <div className="flex space-x-2">
                        <Button 
                          size="sm" 
                          variant="outline" 
                          onClick={() => startEditCategory(category)}
                          data-testid={`edit-category-${category.key}`}
                        >
                          <Edit className="h-4 w-4" />
                        </Button>
                        <AlertDialog>
                          <AlertDialogTrigger asChild>
                            <Button 
                              size="sm" 
                              variant="destructive"
                              data-testid={`delete-category-${category.key}`}
                            >
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </AlertDialogTrigger>
                          <AlertDialogContent>
                            <AlertDialogHeader>
                              <AlertDialogTitle>Delete Category</AlertDialogTitle>
                              <AlertDialogDescription>
                                Are you sure you want to delete "{category.name}"? This action cannot be undone.
                              </AlertDialogDescription>
                            </AlertDialogHeader>
                            <AlertDialogFooter>
                              <AlertDialogCancel>Cancel</AlertDialogCancel>
                              <AlertDialogAction 
                                onClick={() => deleteCategory(category.key)}
                                className="bg-red-600 hover:bg-red-700"
                              >
                                Delete
                              </AlertDialogAction>
                            </AlertDialogFooter>
                          </AlertDialogContent>
                        </AlertDialog>
                      </div>
                    </div>
                  </Card>
                ))}
              </div>
            </div>
          </TabsContent>
          
          {/* ── Users Tab ── */}
          <TabsContent value="users" className="space-y-6">
            {/* Header + Create button */}
            <div className="flex items-center justify-between">
              <div>
                <h3 className="text-lg font-semibold">User Management</h3>
                <p className="text-sm text-muted-foreground mt-1">
                  Create, activate/deactivate, and manage user accounts and roles.
                </p>
              </div>
              <Button onClick={() => setShowCreateUser(true)} data-testid="create-user-btn">
                <UserPlus className="h-4 w-4 mr-2" />
                New User
              </Button>
            </div>

            {/* Create User Form (inline panel) */}
            {showCreateUser && (
              <Card className="p-5 border-primary/40 bg-muted/30">
                <h4 className="font-semibold mb-4 flex items-center gap-2">
                  <UserPlus className="h-4 w-4" /> Create New User
                </h4>
                <form onSubmit={createAdminUser} className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="nu-username">Username</Label>
                    <Input
                      id="nu-username"
                      value={newUserForm.username}
                      onChange={(e) => setNewUserForm({...newUserForm, username: e.target.value})}
                      placeholder="jsmith"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="nu-email">Email</Label>
                    <Input
                      id="nu-email"
                      type="email"
                      value={newUserForm.email}
                      onChange={(e) => setNewUserForm({...newUserForm, email: e.target.value})}
                      placeholder="jsmith@example.com"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="nu-password">Password</Label>
                    <Input
                      id="nu-password"
                      type="password"
                      value={newUserForm.password}
                      onChange={(e) => setNewUserForm({...newUserForm, password: e.target.value})}
                      placeholder="Min 6 characters"
                      required
                    />
                  </div>
                  <div>
                    <Label htmlFor="nu-role">Role</Label>
                    <select
                      id="nu-role"
                      value={newUserForm.role}
                      onChange={(e) => setNewUserForm({...newUserForm, role: e.target.value})}
                      className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md text-sm focus:ring-2 focus:ring-ring"
                    >
                      <option value="user">User</option>
                      <option value="admin">Admin</option>
                    </select>
                  </div>
                  <div className="col-span-2 flex gap-2">
                    <Button type="submit">
                      <Check className="h-4 w-4 mr-1" /> Create User
                    </Button>
                    <Button type="button" variant="outline" onClick={() => { setShowCreateUser(false); setNewUserForm({ username: '', email: '', password: '', role: 'user' }); }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {/* Reset Password Dialog */}
            {showResetPassword && resetPasswordTarget && (
              <Card className="p-5 border-orange-400/50 bg-orange-50 dark:bg-orange-950/20">
                <h4 className="font-semibold mb-3 flex items-center gap-2 text-orange-700 dark:text-orange-400">
                  <Key className="h-4 w-4" /> Reset Password — {resetPasswordTarget.username}
                </h4>
                <form onSubmit={adminResetPassword} className="flex flex-col sm:flex-row gap-3">
                  <Input
                    type="password"
                    value={resetPasswordValue}
                    onChange={(e) => setResetPasswordValue(e.target.value)}
                    placeholder="New password"
                    required
                    className="w-full sm:max-w-xs"
                  />
                  <div className="flex gap-2">
                    <Button type="submit" className="bg-orange-600 hover:bg-orange-700 flex-1 sm:flex-none">
                      <Check className="h-4 w-4 mr-1" /> Set Password
                    </Button>
                    <Button type="button" variant="outline" className="flex-1 sm:flex-none" onClick={() => { setShowResetPassword(false); setResetPasswordTarget(null); setResetPasswordValue(''); }}>
                      Cancel
                    </Button>
                  </div>
                </form>
              </Card>
            )}

            {/* Users Table — desktop */}
            <div className="hidden md:block rounded-lg border border-border overflow-hidden">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-muted text-muted-foreground border-b border-border">
                    <th className="px-4 py-3 text-left font-semibold">Username</th>
                    <th className="px-4 py-3 text-left font-semibold">Email</th>
                    <th className="px-4 py-3 text-left font-semibold">Role</th>
                    <th className="px-4 py-3 text-left font-semibold">Status</th>
                    <th className="px-4 py-3 text-left font-semibold">Msgs</th>
                    <th className="px-4 py-3 text-left font-semibold">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {adminUsers.length === 0 ? (
                    <tr>
                      <td colSpan={6} className="px-4 py-8 text-center text-muted-foreground">
                        No users found. Click "New User" to create one.
                      </td>
                    </tr>
                  ) : (
                    adminUsers.map((u, idx) => (
                      <tr
                        key={u.id}
                        className={`border-b border-border last:border-0 ${idx % 2 === 0 ? '' : 'bg-muted/30'} ${u.id === user?.id ? 'ring-1 ring-inset ring-primary/30' : ''}`}
                      >
                        <td className="px-4 py-3 font-medium text-foreground">
                          {u.username}
                          {u.id === user?.id && (
                            <Badge variant="outline" className="ml-2 text-xs">You</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-muted-foreground">{u.email}</td>
                        <td className="px-4 py-3">
                          <select
                            value={u.role}
                            disabled={u.id === user?.id}
                            onChange={(e) => changeUserRole(u.id, e.target.value)}
                            className="px-2 py-1 border border-input bg-background text-foreground rounded text-xs focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                            title={u.id === user?.id ? "Cannot change your own role" : "Change role"}
                          >
                            <option value="user">user</option>
                            <option value="admin">admin</option>
                          </select>
                        </td>
                        <td className="px-4 py-3">
                          <button
                            onClick={() => toggleUserActive(u.id, u.is_active)}
                            disabled={u.id === user?.id}
                            title={u.id === user?.id ? "Cannot deactivate your own account" : (u.is_active ? "Click to deactivate" : "Click to activate")}
                            className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-medium transition-colors disabled:cursor-not-allowed disabled:opacity-50 ${
                              u.is_active
                                ? 'bg-green-100 text-green-800 hover:bg-green-200 dark:bg-green-900/40 dark:text-green-300'
                                : 'bg-red-100 text-red-700 hover:bg-red-200 dark:bg-red-900/40 dark:text-red-300'
                            }`}
                          >
                            {u.is_active ? <><Check className="h-3 w-3" /> Active</> : <><X className="h-3 w-3" /> Inactive</>}
                          </button>
                        </td>
                        <td className="px-4 py-3 text-muted-foreground text-center">{u.messages_used ?? 0}</td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-1">
                            <Button
                              size="sm"
                              variant="outline"
                              className="h-7 px-2 text-xs"
                              title="Reset Password"
                              onClick={() => { setResetPasswordTarget(u); setShowResetPassword(true); setResetPasswordValue(''); }}
                            >
                              <Key className="h-3 w-3" />
                            </Button>
                            <AlertDialog>
                              <AlertDialogTrigger asChild>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  className="h-7 px-2 text-xs"
                                  disabled={u.id === user?.id}
                                  title={u.id === user?.id ? "Cannot delete your own account" : "Delete User"}
                                >
                                  <Trash2 className="h-3 w-3" />
                                </Button>
                              </AlertDialogTrigger>
                              <AlertDialogContent>
                                <AlertDialogHeader>
                                  <AlertDialogTitle>Delete User</AlertDialogTitle>
                                  <AlertDialogDescription>
                                    Are you sure you want to permanently delete <strong>{u.username}</strong>?
                                    All their chat history will also be removed. This action cannot be undone.
                                  </AlertDialogDescription>
                                </AlertDialogHeader>
                                <AlertDialogFooter>
                                  <AlertDialogCancel>Cancel</AlertDialogCancel>
                                  <AlertDialogAction
                                    onClick={() => deleteAdminUser(u.id, u.username)}
                                    className="bg-red-600 hover:bg-red-700"
                                  >
                                    Delete Permanently
                                  </AlertDialogAction>
                                </AlertDialogFooter>
                              </AlertDialogContent>
                            </AlertDialog>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* Users Card List — mobile */}
            <div className="md:hidden space-y-3">
              {adminUsers.length === 0 ? (
                <p className="text-center text-muted-foreground py-8 text-sm">
                  No users found. Click "New User" to create one.
                </p>
              ) : (
                adminUsers.map((u) => (
                  <Card key={u.id} className={`p-4 ${u.id === user?.id ? 'ring-1 ring-primary/40' : ''}`}>
                    {/* Name + badges */}
                    <div className="flex items-start justify-between gap-2 mb-3">
                      <div className="min-w-0">
                        <p className="font-semibold text-foreground truncate">
                          {u.username}
                          {u.id === user?.id && <Badge variant="outline" className="ml-2 text-xs align-middle">You</Badge>}
                        </p>
                        <p className="text-xs text-muted-foreground truncate mt-0.5">{u.email}</p>
                      </div>
                      <div className="flex items-center gap-1.5 flex-shrink-0">
                        <Badge variant={u.role === 'admin' ? 'default' : 'secondary'} className="text-xs">{u.role}</Badge>
                        <button
                          onClick={() => toggleUserActive(u.id, u.is_active)}
                          disabled={u.id === user?.id}
                          className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium transition-colors disabled:opacity-50 ${
                            u.is_active
                              ? 'bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300'
                              : 'bg-red-100 text-red-700 dark:bg-red-900/40 dark:text-red-300'
                          }`}
                        >
                          {u.is_active ? <Check className="h-3 w-3" /> : <X className="h-3 w-3" />}
                          {u.is_active ? 'Active' : 'Inactive'}
                        </button>
                      </div>
                    </div>

                    {/* Role changer + msgs */}
                    <div className="flex items-center gap-3 mb-3">
                      <div className="flex items-center gap-1.5">
                        <span className="text-xs text-muted-foreground">Role:</span>
                        <select
                          value={u.role}
                          disabled={u.id === user?.id}
                          onChange={(e) => changeUserRole(u.id, e.target.value)}
                          className="px-2 py-1 border border-input bg-background text-foreground rounded text-xs focus:ring-1 focus:ring-ring disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                          <option value="user">user</option>
                          <option value="admin">admin</option>
                        </select>
                      </div>
                      <span className="text-xs text-muted-foreground">
                        {u.messages_used ?? 0} msgs used
                      </span>
                    </div>

                    {/* Actions */}
                    <div className="flex gap-2">
                      <Button
                        size="sm"
                        variant="outline"
                        className="flex-1 text-xs"
                        onClick={() => { setResetPasswordTarget(u); setShowResetPassword(true); setResetPasswordValue(''); }}
                      >
                        <Key className="h-3 w-3 mr-1" /> Reset Password
                      </Button>
                      <AlertDialog>
                        <AlertDialogTrigger asChild>
                          <Button
                            size="sm"
                            variant="destructive"
                            className="flex-1 text-xs"
                            disabled={u.id === user?.id}
                          >
                            <Trash2 className="h-3 w-3 mr-1" /> Delete
                          </Button>
                        </AlertDialogTrigger>
                        <AlertDialogContent>
                          <AlertDialogHeader>
                            <AlertDialogTitle>Delete User</AlertDialogTitle>
                            <AlertDialogDescription>
                              Are you sure you want to permanently delete <strong>{u.username}</strong>?
                              All their chat history will also be removed. This action cannot be undone.
                            </AlertDialogDescription>
                          </AlertDialogHeader>
                          <AlertDialogFooter>
                            <AlertDialogCancel>Cancel</AlertDialogCancel>
                            <AlertDialogAction
                              onClick={() => deleteAdminUser(u.id, u.username)}
                              className="bg-red-600 hover:bg-red-700"
                            >
                              Delete Permanently
                            </AlertDialogAction>
                          </AlertDialogFooter>
                        </AlertDialogContent>
                      </AlertDialog>
                    </div>
                  </Card>
                ))
              )}
            </div>

            <div className="flex items-center justify-between text-xs text-muted-foreground">
              <span>{adminUsers.length} user{adminUsers.length !== 1 ? 's' : ''} total</span>
              <Button size="sm" variant="outline" onClick={loadAdminUsers} className="text-xs h-7">
                <RefreshCw className="h-3 w-3 mr-1" /> Refresh
              </Button>
            </div>
          </TabsContent>

          <TabsContent value="welcome">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Welcome Message</h3>
                <Button 
                  onClick={() => {
                    setEditWelcomeData({
                      title: welcomeMessage.title,
                      description: welcomeMessage.description,
                      icon: welcomeMessage.icon
                    });
                    setShowWelcomeEdit(true);
                  }}
                  data-testid="edit-welcome-btn"
                >
                  <Palette className="h-4 w-4 mr-2" />
                  Edit Welcome Message
                </Button>
              </div>
              
              <div className="border rounded-lg p-4 bg-muted/50">
                <div className="text-center">
                  <div className="text-4xl mb-4">{welcomeMessage.icon || '👋'}</div>
                  <h3 className="text-xl font-semibold text-foreground mb-2">
                    {welcomeMessage.title}
                  </h3>
                  <p className="text-xs text-muted-foreground mb-4">Developed by RG Integrated Solutions</p>
                  <p className="text-muted-foreground max-w-md mx-auto">
                    {welcomeMessage.description}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="content">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Landing Page Content</h3>
                <Button 
                  onClick={() => {
                    setEditContentData({ 
                      title: landingContent.title || 'Tech Assistant',
                      description: landingContent.description || 'Professional IT support powered by AI...',
                      icon: landingContent.icon || '🎯',
                      icon_image_url: landingContent.icon_image_url || '',
                      content: landingContent.content || '',
                      banner_image_url: landingContent.banner_image_url || ''
                    });
                    setShowContentEdit(true);
                  }}
                  data-testid="edit-content-btn"
                >
                  <Edit className="h-4 w-4 mr-2" />
                  Edit Landing Page
                </Button>
              </div>
              
              <div className="space-y-4">
                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                  <div className="border rounded-lg p-4 bg-muted/40">
                    <h4 className="font-medium text-sm mb-2">Title</h4>
                    <p className="text-muted-foreground">
                      {landingContent.title || 'Tech Assistant'}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4 bg-muted/40">
                    <h4 className="font-medium text-sm mb-2">Icon</h4>
                    <div className="flex items-center space-x-2">
                      {landingContent.icon_image_url ? (
                        <img 
                          src={`${API_BASE_URL}${landingContent.icon_image_url}`} 
                          alt="Landing icon" 
                          className="w-8 h-8 object-cover rounded"
                        />
                      ) : (
                        <span className="text-2xl">{landingContent.icon || '🎯'}</span>
                      )}
                      <span className="text-muted-foreground text-sm">
                        {landingContent.icon_image_url ? 'Custom Image' : (landingContent.icon || '🎯')}
                      </span>
                    </div>
                  </div>
                </div>
                
                <div className="border rounded-lg p-4 bg-muted/40">
                  <h4 className="font-medium text-sm mb-2">Description</h4>
                  <p className="text-muted-foreground">
                    {landingContent.description || 'Professional IT support powered by AI. Get expert help with M365, Windows, MacOS, Azure, and more.'}
                  </p>
                </div>

                {landingContent.banner_image_url && (
                  <div className="border rounded-lg p-4 bg-muted/40">
                    <h4 className="font-medium text-sm mb-2">Banner Image</h4>
                    <img 
                      src={`${API_BASE_URL}${landingContent.banner_image_url}`} 
                      alt="Landing banner" 
                      className="w-full max-w-md rounded-lg shadow-sm"
                    />
                  </div>
                )}
                
                {landingContent.content && (
                  <div className="border rounded-lg p-4 bg-muted/40">
                    <h4 className="font-medium text-sm mb-2">Additional Content</h4>
                    <p className="text-muted-foreground">
                      {landingContent.content}
                    </p>
                  </div>
                )}
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="theme">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">Theme Settings</h3>
                <Badge variant="secondary">
                  <Palette className="h-3 w-3 mr-1" />
                  Current: {currentTheme.charAt(0).toUpperCase() + currentTheme.slice(1)}
                </Badge>
              </div>
              
              <div className="space-y-6">
                <div>
                  <Label className="text-base font-medium mb-3 block">Select Application Theme</Label>
                  <p className="text-sm text-muted-foreground mb-4">
                    Choose the visual theme for the entire application. Changes apply immediately to all users.
                  </p>
                  
                  <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                    {/* Default Theme */}
                    <Card className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      currentTheme === 'default' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-muted'
                    }`} onClick={() => changeTheme('default')}>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-blue-500 to-blue-600 rounded"></div>
                        <div>
                          <div className="font-medium">Default</div>
                          <div className="text-xs text-muted-foreground">Clean & Professional</div>
                        </div>
                        {currentTheme === 'default' && <Check className="h-4 w-4 text-blue-600 ml-auto" />}
                      </div>
                    </Card>
                    
                    {/* Dark Theme */}
                    <Card className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      currentTheme === 'dark' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-muted'
                    }`} onClick={() => changeTheme('dark')}>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-gradient-to-br from-slate-700 to-slate-900 rounded"></div>
                        <div>
                          <div className="font-medium">Dark</div>
                          <div className="text-xs text-muted-foreground">Easy on the Eyes</div>
                        </div>
                        {currentTheme === 'dark' && <Check className="h-4 w-4 text-blue-600 ml-auto" />}
                      </div>
                    </Card>
                    
                    {/* Terminal Theme */}
                    <Card className={`p-4 cursor-pointer transition-all hover:shadow-md ${
                      currentTheme === 'terminal' ? 'ring-2 ring-blue-500 bg-blue-50' : 'hover:bg-muted'
                    }`} onClick={() => changeTheme('terminal')}>
                      <div className="flex items-center space-x-3">
                        <div className="w-8 h-8 bg-black border-2 border-green-500 rounded flex items-center justify-center">
                          <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        </div>
                        <div>
                          <div className="font-medium">Terminal</div>
                          <div className="text-xs text-muted-foreground">Retro Matrix Style</div>
                        </div>
                        {currentTheme === 'terminal' && <Check className="h-4 w-4 text-blue-600 ml-auto" />}
                      </div>
                    </Card>
                  </div>
                </div>
                
                <div className="border-t pt-4">
                  <h4 className="font-medium mb-2">Theme Preview</h4>
                  <div className={`p-4 rounded-lg border theme-${currentTheme}`}>
                    <div className="text-sm font-medium mb-2">Sample Interface</div>
                    <div className="flex space-x-2 mb-3">
                      <div className="px-3 py-1 bg-blue-600 text-white text-xs rounded">Button</div>
                      <div className="px-3 py-1 border text-xs rounded">Outline</div>
                    </div>
                    <div className="text-xs text-muted-foreground">
                      This is how the interface will look with the {currentTheme} theme applied.
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="settings">
            <Card className="p-6">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-semibold">System Settings</h3>
                <Button 
                  onClick={() => {
                    setEditAdminSettings(adminSettings);
                    setShowAdminSettings(true);
                  }}
                  data-testid="edit-settings-btn"
                >
                  <Settings className="h-4 w-4 mr-2" />
                  Edit Settings
                </Button>
              </div>
              
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Sandbox Mode</h4>
                  <p className="text-sm text-muted-foreground">
                    {adminSettings.sandbox_mode ? 'Enabled (Test Mode)' : 'Disabled (Live Mode)'}
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Monthly Price</h4>
                  <p className="text-sm text-muted-foreground">
                    ${adminSettings.monthly_price || '20.00'} USD
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Yearly Price</h4>
                  <p className="text-sm text-muted-foreground">
                    ${adminSettings.yearly_price || '100.00'} USD
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Stripe Keys</h4>
                  <p className="text-sm text-muted-foreground">
                    {adminSettings.stripe_secret_key ? 'Configured' : 'Not configured'}
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">Gmail API</h4>
                  <p className="text-sm text-muted-foreground">
                    {adminSettings.gmail_api_key ? 'Configured' : 'Not configured'}
                  </p>
                </div>
                <div className="border rounded-lg p-4">
                  <h4 className="font-medium mb-2">PayPal</h4>
                  <p className="text-sm text-muted-foreground">
                    {adminSettings.paypal_client_id ? 'Configured' : 'Not configured'}
                  </p>
                </div>
              </div>
            </Card>
          </TabsContent>

          <TabsContent value="stats" className="space-y-6">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-semibold">Usage Statistics</h3>
              <Button size="sm" variant="outline" onClick={loadAdminStats} disabled={loadingStats}>
                {loadingStats ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : <RefreshCw className="h-3 w-3 mr-1" />}
                Refresh
              </Button>
            </div>

            {!adminStats ? (
              <div className="text-center py-12 text-muted-foreground">
                <BarChart2 className="h-10 w-10 mx-auto mb-3 opacity-30" />
                <p className="text-sm">Click Refresh to load statistics</p>
              </div>
            ) : (
              <>
                {/* Summary cards */}
                <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
                  {[
                    { label: 'Total Users', value: adminStats.total_users, color: 'text-blue-600 dark:text-blue-400' },
                    { label: 'Paid Users', value: adminStats.paid_users, color: 'text-green-600 dark:text-green-400' },
                    { label: 'Messages Today', value: adminStats.messages_today, color: 'text-purple-600 dark:text-purple-400' },
                    { label: 'Total Messages', value: adminStats.total_messages, color: 'text-orange-600 dark:text-orange-400' },
                  ].map(stat => (
                    <Card key={stat.label} className="p-4 text-center">
                      <p className={`text-2xl font-bold ${stat.color}`}>{stat.value?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground mt-1">{stat.label}</p>
                    </Card>
                  ))}
                </div>

                {/* Secondary stats */}
                <div className="grid grid-cols-2 sm:grid-cols-3 gap-3">
                  {[
                    { label: 'Messages This Week', value: adminStats.messages_week },
                    { label: 'Messages This Month', value: adminStats.messages_month },
                    { label: 'Verified Users', value: adminStats.verified_users },
                    { label: 'Admin Users', value: adminStats.admin_users },
                  ].map(stat => (
                    <div key={stat.label} className="border border-border rounded-lg p-3">
                      <p className="text-lg font-semibold text-foreground">{stat.value?.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">{stat.label}</p>
                    </div>
                  ))}
                </div>

                {/* Messages per day bar chart */}
                <Card className="p-4">
                  <h4 className="text-sm font-semibold mb-4 text-foreground">Messages Per Day (last 14 days)</h4>
                  <div className="flex items-end gap-1 h-32">
                    {(adminStats.messages_per_day || []).map((d, i) => {
                      const max = Math.max(...adminStats.messages_per_day.map(x => x.count), 1);
                      const h = Math.max(4, (d.count / max) * 100);
                      return (
                        <div key={i} className="flex-1 flex flex-col items-center gap-1">
                          <span className="text-xs text-muted-foreground">{d.count > 0 ? d.count : ''}</span>
                          <div
                            className="w-full rounded-t bg-primary/70 hover:bg-primary transition-colors"
                            style={{ height: `${h}%` }}
                            title={`${d.date}: ${d.count} messages`}
                          />
                          <span className="text-[9px] text-muted-foreground leading-tight text-center" style={{writingMode:'vertical-rl',transform:'rotate(180deg)',height:'30px'}}>{d.date}</span>
                        </div>
                      );
                    })}
                  </div>
                </Card>

                {/* Agent usage */}
                {adminStats.agent_usage?.length > 0 && (
                  <Card className="p-4">
                    <h4 className="text-sm font-semibold mb-3 text-foreground">Top Agents Used</h4>
                    <div className="space-y-2">
                      {adminStats.agent_usage.map((a, i) => {
                        const max = adminStats.agent_usage[0]?.count || 1;
                        return (
                          <div key={i} className="flex items-center gap-2">
                            <span className="text-xs text-muted-foreground w-28 truncate capitalize">{a.agent}</span>
                            <div className="flex-1 bg-muted rounded-full h-2">
                              <div className="bg-primary rounded-full h-2 transition-all" style={{ width: `${(a.count/max)*100}%` }} />
                            </div>
                            <span className="text-xs text-muted-foreground w-8 text-right">{a.count}</span>
                          </div>
                        );
                      })}
                    </div>
                  </Card>
                )}
              </>
            )}
          </TabsContent>
        </Tabs>
      </DialogContent>
    </Dialog>
  );

  // If not authenticated, show the public landing page
  if (!user) {
    return (
      <>
        <LandingPage
          currentTheme={currentTheme}
          toggleTheme={toggleTheme}
          onOpenAuth={(mode) => { setAuthMode(mode); setShowAuthModal(true); }}
          landingContent={landingContent}
          API_BASE_URL={API_BASE_URL}
        />

        {/* Auth Modal */}
        <Dialog open={showAuthModal} onOpenChange={setShowAuthModal}>
          <DialogContent className="sm:max-w-md">
            <DialogHeader>
              <DialogTitle className="text-center text-xl">
                {authMode === 'login' ? '👋 Welcome Back' : '🚀 Create Your Account'}
              </DialogTitle>
            </DialogHeader>

            <div className="flex border-b border-border mb-2">
              <button
                className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors ${authMode === 'login' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                onClick={() => setAuthMode('login')}
              >Login</button>
              <button
                className={`flex-1 py-2.5 text-sm font-semibold border-b-2 transition-colors ${authMode === 'register' ? 'border-primary text-primary' : 'border-transparent text-muted-foreground hover:text-foreground'}`}
                onClick={() => setAuthMode('register')}
              >Register</button>
            </div>

            {forgotPasswordMode ? (
              <div className="space-y-4 pt-1">
                {forgotPasswordSent ? (
                  <div className="text-center py-4">
                    <div className="text-4xl mb-3">📧</div>
                    <p className="text-sm text-foreground font-medium">Check your inbox</p>
                    <p className="text-xs text-muted-foreground mt-1">If that email is registered, a reset link has been sent.</p>
                    <Button className="mt-4 w-full" onClick={() => { setForgotPasswordMode(false); setForgotPasswordSent(false); setForgotPasswordEmail(''); }}>
                      Back to Login
                    </Button>
                  </div>
                ) : (
                  <form onSubmit={handleForgotPassword} className="space-y-4">
                    <p className="text-sm text-muted-foreground">Enter your email and we'll send you a reset link.</p>
                    <div>
                      <Label htmlFor="forgot-email">Email Address</Label>
                      <Input id="forgot-email" type="email" value={forgotPasswordEmail} onChange={(e) => setForgotPasswordEmail(e.target.value)} placeholder="your@email.com" required />
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Mail className="h-4 w-4 mr-2" />}
                      Send Reset Link
                    </Button>
                    <button type="button" className="w-full text-xs text-muted-foreground hover:text-foreground" onClick={() => { setForgotPasswordMode(false); setForgotPasswordEmail(''); }}>
                      ← Back to login
                    </button>
                  </form>
                )}
              </div>
            ) : resetPasswordToken ? (
              <div className="space-y-4 pt-1">
                {resetPasswordDone ? (
                  <div className="text-center py-4">
                    <div className="text-4xl mb-3">✅</div>
                    <p className="text-sm font-medium">Password reset successfully!</p>
                    <p className="text-xs text-muted-foreground mt-1">Redirecting to login...</p>
                  </div>
                ) : (
                  <form onSubmit={handleResetPassword} className="space-y-4">
                    <p className="text-sm text-muted-foreground">Enter your new password below.</p>
                    <div>
                      <Label>New Password</Label>
                      <Input type="password" value={resetPasswordNewPass} onChange={(e) => setResetPasswordNewPass(e.target.value)} placeholder="Min 6 characters" required />
                    </div>
                    <div>
                      <Label>Confirm New Password</Label>
                      <Input type="password" value={resetPasswordConfirm} onChange={(e) => setResetPasswordConfirm(e.target.value)} placeholder="Repeat new password" required />
                    </div>
                    <Button type="submit" className="w-full" disabled={isLoading}>
                      {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : <Key className="h-4 w-4 mr-2" />}
                      Reset Password
                    </Button>
                  </form>
                )}
              </div>
            ) : (
              <form onSubmit={handleAuth} className="space-y-4 pt-1">
                {authMode === 'login' ? (
                  <>
                    <div>
                      <Label htmlFor="modal-identifier">Username or Email</Label>
                      <Input id="modal-identifier" type="text" value={loginForm.identifier} onChange={(e) => setLoginForm({...loginForm, identifier: e.target.value})} placeholder="Enter username or email" required data-testid="login-identifier" />
                    </div>
                    <div>
                      <Label htmlFor="modal-password">Password</Label>
                      <Input id="modal-password" type="password" value={loginForm.password} onChange={(e) => setLoginForm({...loginForm, password: e.target.value})} placeholder="Enter password" required data-testid="login-password" />
                    </div>
                    <div className="flex justify-end">
                      <button type="button" className="text-xs text-primary hover:underline" onClick={() => setForgotPasswordMode(true)}>
                        Forgot password?
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div>
                      <Label htmlFor="modal-username">Username</Label>
                      <Input id="modal-username" type="text" value={registerForm.username} onChange={(e) => setRegisterForm({...registerForm, username: e.target.value})} placeholder="Choose a username" required data-testid="register-username" />
                    </div>
                    <div>
                      <Label htmlFor="modal-email">Email</Label>
                      <Input id="modal-email" type="email" value={registerForm.email} onChange={(e) => setRegisterForm({...registerForm, email: e.target.value})} placeholder="Enter your email" required data-testid="register-email" />
                    </div>
                    <div>
                      <Label htmlFor="modal-reg-password">Password</Label>
                      <Input id="modal-reg-password" type="password" value={registerForm.password} onChange={(e) => setRegisterForm({...registerForm, password: e.target.value})} placeholder="Create a password (min 6 chars)" required data-testid="register-password" />
                    </div>
                  </>
                )}
                <Button type="submit" className="w-full py-5 font-bold" disabled={isLoading} data-testid="auth-submit">
                  {isLoading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                  {isLoading ? 'Please wait...' : (authMode === 'login' ? 'Sign In to Dashboard' : 'Create Free Account')}
                </Button>
              </form>
            )}

            <p className="text-center text-xs text-muted-foreground pt-1">
              {authMode === 'login' ? "Don't have an account? " : 'Already have an account? '}
              <button className="text-primary hover:underline font-semibold" onClick={() => setAuthMode(authMode === 'login' ? 'register' : 'login')}>
                {authMode === 'login' ? 'Sign up free →' : 'Sign in'}
              </button>
            </p>

            {authMode === 'register' && (
              <p className="text-center text-xs text-muted-foreground border-t border-border pt-3">
                ✨ Start with <strong>5 free messages</strong> — no credit card required
              </p>
            )}
          </DialogContent>
        </Dialog>
      </>
    );
  }

  // Landing Content Edit Dialog
  const LandingContentEditDialog = () => {
    const handleIconImageUpload = (event) => {
      const file = event.target.files[0];
      const width = imageResizeOptions.width ? parseInt(imageResizeOptions.width) : 64;
      const height = imageResizeOptions.height ? parseInt(imageResizeOptions.height) : 64;
      
      handleFileUpload(file, (url) => {
        setEditContentData(prev => ({ ...prev, icon_image_url: url }));
      }, width, height);
    };

    const handleBannerImageUpload = (event) => {
      const file = event.target.files[0];
      const width = imageResizeOptions.width ? parseInt(imageResizeOptions.width) : null;
      const height = imageResizeOptions.height ? parseInt(imageResizeOptions.height) : null;
      
      handleFileUpload(file, (url) => {
        setEditContentData(prev => ({ ...prev, banner_image_url: url }));
      }, width, height);
    };

    return (
      <Dialog open={showContentEdit} onOpenChange={setShowContentEdit}>
        <DialogContent className="sm:max-w-3xl max-h-[90vh] overflow-y-auto" data-testid="content-edit-dialog">
          <DialogHeader>
            <DialogTitle>Edit Landing Page Content</DialogTitle>
          </DialogHeader>
          <div className="space-y-6">
            
            {/* Title and Icon Section */}
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label htmlFor="landing-title-input">Landing Page Title</Label>
                <Input
                  id="landing-title-input"
                  value={editContentData.title || ''}
                  onChange={(e) => setEditContentData({...editContentData, title: e.target.value})}
                  placeholder="Tech Assistant"
                  data-testid="landing-title-input"
                />
              </div>
              <div>
                <Label htmlFor="landing-icon-input">Icon (Emoji)</Label>
                <Input
                  id="landing-icon-input"
                  value={editContentData.icon || ''}
                  onChange={(e) => setEditContentData({...editContentData, icon: e.target.value})}
                  placeholder="🎯"
                  data-testid="landing-icon-input"
                />
              </div>
            </div>

            {/* Icon Image Upload */}
            <div>
              <Label>Or Upload Icon Image</Label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleIconImageUpload}
                  disabled={uploadingFile}
                  data-testid="landing-icon-image-upload"
                />
                {editContentData.icon_image_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={`${API_BASE_URL}${editContentData.icon_image_url}`} 
                      alt="Icon preview" 
                      className="w-12 h-12 object-cover rounded"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditContentData(prev => ({ ...prev, icon_image_url: '' }))}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Description */}
            <div>
              <Label htmlFor="landing-description-input">Description</Label>
              <Textarea
                id="landing-description-input"
                value={editContentData.description || ''}
                onChange={(e) => setEditContentData({...editContentData, description: e.target.value})}
                placeholder="Professional IT support powered by AI. Get expert help with M365, Windows, MacOS, Azure, and more."
                rows={3}
                data-testid="landing-description-input"
              />
            </div>

            {/* Additional Content (Legacy field) */}
            <div>
              <Label htmlFor="landing-content-input">Additional Content (Optional)</Label>
              <Textarea
                id="landing-content-input"
                value={editContentData.content || ''}
                onChange={(e) => setEditContentData({...editContentData, content: e.target.value})}
                placeholder="Any additional content for the landing page"
                rows={2}
                data-testid="landing-content-input"
              />
            </div>
            
            {/* Banner Image Upload */}
            <div>
              <Label>Banner Image (Optional)</Label>
              <div className="space-y-2">
                <Input
                  type="file"
                  accept="image/*"
                  onChange={handleBannerImageUpload}
                  disabled={uploadingFile}
                  data-testid="banner-image-upload"
                />
                {editContentData.banner_image_url && (
                  <div className="flex items-center space-x-2">
                    <img 
                      src={`${API_BASE_URL}${editContentData.banner_image_url}`} 
                      alt="Banner preview" 
                      className="w-20 h-12 object-cover rounded"
                    />
                    <Button
                      size="sm"
                      variant="outline"
                      onClick={() => setEditContentData(prev => ({ ...prev, banner_image_url: '' }))}
                    >
                      <X className="h-3 w-3" />
                    </Button>
                  </div>
                )}
              </div>
            </div>
            
            {/* Image Resize Options */}
            <div className="border-t pt-4">
              <Label className="text-sm font-medium mb-2">Image Resize Options (Optional)</Label>
              <div className="grid grid-cols-2 gap-2">
                <div>
                  <Label htmlFor="resize-width-banner">Max Width (px)</Label>
                  <Input
                    id="resize-width-banner"
                    type="number"
                    placeholder="e.g., 400"
                    value={imageResizeOptions.width}
                    onChange={(e) => setImageResizeOptions(prev => ({ ...prev, width: e.target.value }))}
                  />
                </div>
                <div>
                  <Label htmlFor="resize-height-banner">Max Height (px)</Label>
                  <Input
                    id="resize-height-banner"
                    type="number"
                    placeholder="e.g., 300"
                    value={imageResizeOptions.height}
                    onChange={(e) => setImageResizeOptions(prev => ({ ...prev, height: e.target.value }))}
                  />
                </div>
              </div>
            </div>

            {/* Preview Section */}
            <div className="border-t pt-4">
              <Label className="text-base font-medium mb-3 block">Preview</Label>
              <Card className="p-6 bg-muted/50 border-border">
                <div className="text-center">
                  <div className="text-4xl mb-4">
                    {editContentData.icon_image_url ? (
                      <img 
                        src={`${API_BASE_URL}${editContentData.icon_image_url}`} 
                        alt="Icon preview" 
                        className="w-16 h-16 mx-auto object-cover rounded-lg"
                      />
                    ) : (
                      editContentData.icon || '🎯'
                    )}
                  </div>
                  <h1 className="text-2xl font-bold text-foreground mb-2">
                    {editContentData.title || 'Tech Assistant'}
                  </h1>
                  <p className="text-muted-foreground mb-4 max-w-lg mx-auto">
                    {editContentData.description || 'Professional IT support powered by AI. Get expert help with M365, Windows, MacOS, Azure, and more.'}
                  </p>
                  {editContentData.banner_image_url && (
                    <img 
                      src={`${API_BASE_URL}${editContentData.banner_image_url}`} 
                      alt="Banner preview" 
                      className="w-full max-w-md mx-auto rounded-lg shadow-sm"
                    />
                  )}
                </div>
              </Card>
            </div>

            <div className="flex space-x-2 pt-4 border-t">
              <Button onClick={saveLandingContent} data-testid="save-content">
                <Save className="h-4 w-4 mr-2" />
                Save Landing Page
              </Button>
              <Button 
                variant="outline" 
                onClick={() => setShowContentEdit(false)}
              >
                Cancel
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    );
  };

  // Admin Settings Dialog
  const AdminSettingsDialog = () => (
    <Dialog open={showAdminSettings} onOpenChange={setShowAdminSettings}>
      <DialogContent className="sm:max-w-2xl" data-testid="settings-dialog">
        <DialogHeader>
          <DialogTitle>System Settings</DialogTitle>
        </DialogHeader>
        <div className="space-y-6">
          <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
            <div>
              <Label className="mb-1">Sandbox Mode</Label>
              <select
                value={editAdminSettings.sandbox_mode}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, sandbox_mode: e.target.value === 'true'})}
                className="w-full px-3 py-2 border border-input bg-background text-foreground rounded-md focus:ring-2 focus:ring-ring text-sm"
              >
                <option value="true">Test Mode (Sandbox)</option>
                <option value="false">Live Mode (Production)</option>
              </select>
            </div>
            <div>
              <Label className="mb-1">Monthly Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={editAdminSettings.monthly_price || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, monthly_price: parseFloat(e.target.value) || 0})}
                placeholder="20.00"
                data-testid="monthly-price-input"
              />
            </div>
            <div>
              <Label className="mb-1">Yearly Price ($)</Label>
              <Input
                type="number"
                step="0.01"
                min="0"
                value={editAdminSettings.yearly_price || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, yearly_price: parseFloat(e.target.value) || 0})}
                placeholder="100.00"
                data-testid="yearly-price-input"
              />
            </div>
          </div>

          <div className="grid grid-cols-1 gap-4">
            {/* SMTP Email Settings */}
            <div className="border border-border rounded-lg p-4 space-y-3">
              <h4 className="text-sm font-semibold text-foreground flex items-center gap-2">
                <Mail className="h-4 w-4" /> Email (SMTP) Settings
              </h4>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                <div>
                  <Label className="mb-1">SMTP Host</Label>
                  <Input value={editAdminSettings.smtp_host || ''} onChange={(e) => setEditAdminSettings({...editAdminSettings, smtp_host: e.target.value})} placeholder="smtp.gmail.com" />
                </div>
                <div>
                  <Label className="mb-1">SMTP Port</Label>
                  <Input type="number" value={editAdminSettings.smtp_port || 587} onChange={(e) => setEditAdminSettings({...editAdminSettings, smtp_port: parseInt(e.target.value)})} placeholder="587" />
                </div>
                <div>
                  <Label className="mb-1">SMTP Username (Email)</Label>
                  <Input type="email" value={editAdminSettings.smtp_user || ''} onChange={(e) => setEditAdminSettings({...editAdminSettings, smtp_user: e.target.value})} placeholder="your@gmail.com" />
                </div>
                <div>
                  <Label className="mb-1">SMTP Password / App Password</Label>
                  <Input type="password" value={editAdminSettings.smtp_password || ''} onChange={(e) => setEditAdminSettings({...editAdminSettings, smtp_password: e.target.value})} placeholder="Gmail App Password" />
                </div>
                <div>
                  <Label className="mb-1">From Name</Label>
                  <Input value={editAdminSettings.smtp_from_name || ''} onChange={(e) => setEditAdminSettings({...editAdminSettings, smtp_from_name: e.target.value})} placeholder="Tech Assistant" />
                </div>
                <div>
                  <Label className="mb-1">Site URL</Label>
                  <Input value={editAdminSettings.site_url || ''} onChange={(e) => setEditAdminSettings({...editAdminSettings, site_url: e.target.value})} placeholder="https://techassistant.infoplay.com" />
                </div>
              </div>
            </div>

            <div>
              <Label className="mb-1">Stripe Publishable Key</Label>
              <Input
                type="text"
                value={editAdminSettings.stripe_publishable_key || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, stripe_publishable_key: e.target.value})}
                placeholder="pk_test_..."
              />
            </div>
            <div>
              <Label className="mb-1">Stripe Secret Key</Label>
              <Input
                type="password"
                value={editAdminSettings.stripe_secret_key || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, stripe_secret_key: e.target.value})}
                placeholder="sk_test_..."
              />
            </div>
            <div>
              <Label className="mb-1">Gmail API Key</Label>
              <Input
                type="password"
                value={editAdminSettings.gmail_api_key || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, gmail_api_key: e.target.value})}
                placeholder="Gmail API Key"
              />
            </div>
            <div>
              <Label className="mb-1">PayPal Client ID</Label>
              <Input
                type="text"
                value={editAdminSettings.paypal_client_id || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, paypal_client_id: e.target.value})}
                placeholder="PayPal Client ID"
              />
            </div>
            <div>
              <Label className="mb-1">PayPal Secret</Label>
              <Input
                type="password"
                value={editAdminSettings.paypal_secret || ''}
                onChange={(e) => setEditAdminSettings({...editAdminSettings, paypal_secret: e.target.value})}
                placeholder="PayPal Secret"
              />
            </div>
          </div>
          
          <div className="flex space-x-2">
            <Button onClick={saveAdminSettings} data-testid="save-settings">
              <Save className="h-4 w-4 mr-2" />
              Save Settings
            </Button>
            <Button 
              variant="outline" 
              onClick={() => setShowAdminSettings(false)}
            >
              Cancel
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );

  return (
    <div className="flex h-screen bg-background" data-testid="helpdesk-app">
      {/* Desktop Sidebar */}
      {!isMobile && (
        <div className="w-80 hidden md:block">
          <AgentSidebar />
        </div>
      )}

      {/* Mobile Sidebar */}
      {isMobile && (
        <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
          <SheetContent side="left" className="w-80 p-0">
            <AgentSidebar />
          </SheetContent>
        </Sheet>
      )}

      {/* Main Chat Area */}
      <div className="flex-1 min-w-0 flex flex-col overflow-x-hidden">
        {/* Header */}
        <div className="bg-card border-b border-border px-3 sm:px-6 py-3 sm:py-4 flex items-center justify-between gap-2">
          {/* Left: hamburger + agent name */}
          <div className="flex items-center gap-2 min-w-0">
            {isMobile && (
              <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
                <SheetTrigger asChild>
                  <Button variant="ghost" size="icon" data-testid="mobile-menu-btn">
                    <Menu className="h-5 w-5" />
                  </Button>
                </SheetTrigger>
              </Sheet>
            )}
            <div className="min-w-0">
              <h1 className="text-base sm:text-xl font-semibold text-foreground truncate">
                {getCurrentAgentName()}
              </h1>
              <Badge variant="secondary" className="mt-0.5 text-xs hidden sm:inline-flex">
                Active Session
              </Badge>
            </div>
          </div>

          {/* Right: actions */}
          <div className="flex items-center gap-1 sm:gap-2 flex-shrink-0">
            {user && (
              <>
                {/* Desktop: full user info pill */}
                <div className="hidden md:flex items-center gap-2 px-3 py-1.5 bg-muted rounded-lg">
                  <span className="text-sm font-medium text-foreground">{user.username}</span>
                  <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="text-xs">
                    {user.role}
                  </Badge>
                  {user.role === 'admin' ? (
                    <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Unlimited</Badge>
                  ) : user.has_paid ? (
                    <Badge className="text-xs bg-green-100 text-green-800 dark:bg-green-900/40 dark:text-green-300">Unlimited</Badge>
                  ) : (
                    <Badge variant="outline" className="text-xs">
                      {messagesRemaining !== null ? `${messagesRemaining} left` : `${5 - (user.messages_used || 0)} trial left`}
                    </Badge>
                  )}
                </div>

                {/* Mobile: just username badge */}
                <div className="flex md:hidden items-center gap-1 px-2 py-1 bg-muted rounded-md">
                  <span className="text-xs font-medium text-foreground max-w-[80px] truncate">{user.username}</span>
                  <Badge variant={user.role === 'admin' ? 'default' : 'secondary'} className="text-xs px-1">
                    {user.role}
                  </Badge>
                </div>

                <Button size="icon" variant="ghost" onClick={() => setShowPasswordChange(true)}
                  data-testid="change-password-btn" title="Change Password" className="h-8 w-8">
                  <Key className="h-4 w-4" />
                </Button>
                {user.role === 'admin' && (
                  <Button size="icon" variant="ghost"
                    onClick={() => { setShowAdmin(true); loadCategories(); loadAdminUsers(); loadAdminStats(); }}
                    data-testid="admin-panel-btn" title="Admin Panel" className="h-8 w-8">
                    <Settings className="h-4 w-4" />
                  </Button>
                )}
                <Button size="icon" variant="ghost" onClick={logout}
                  data-testid="logout-btn" title="Logout" className="h-8 w-8">
                  <LogOut className="h-4 w-4" />
                </Button>
              </>
            )}

            {user && messages.length > 0 && (
              <Button onClick={exportChat} variant="outline" size="icon" className="h-8 w-8"
                title="Export Chat" data-testid="export-chat-btn">
                <Download className="h-4 w-4" />
              </Button>
            )}

            <Button onClick={toggleTheme} variant="outline" size="icon" className="h-8 w-8"
              title={currentTheme === 'dark' ? 'Switch to Light Mode' : 'Switch to Dark Mode'}>
              {currentTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
            </Button>

            <Button onClick={clearChat} variant="outline" size="icon" className="h-8 w-8"
              data-testid="clear-chat-btn" title="Clear Chat">
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        </div>

        {/* Email verification banner */}
        {user && user.email_verified === false && (
          <div className="bg-amber-500/10 border-b border-amber-500/30 px-4 py-2 flex items-center justify-between gap-2 flex-wrap">
            <p className="text-xs text-amber-700 dark:text-amber-400 flex items-center gap-1.5">
              <Mail className="h-3.5 w-3.5" />
              Please verify your email address to keep your account active.
            </p>
            <Button
              size="sm"
              variant="outline"
              className="h-7 text-xs border-amber-500/50 text-amber-700 dark:text-amber-400 hover:bg-amber-500/10"
              onClick={handleResendVerification}
              disabled={resendingVerification}
            >
              {resendingVerification ? <Loader2 className="h-3 w-3 animate-spin mr-1" /> : null}
              Resend verification
            </Button>
          </div>
        )}

        {/* Email verify status toast */}
        {emailVerifyStatus && (
          <div className={`px-4 py-2 text-sm flex items-center gap-2 ${emailVerifyStatus === 'success' ? 'bg-green-500/10 text-green-700 dark:text-green-400 border-b border-green-500/30' : emailVerifyStatus === 'error' ? 'bg-red-500/10 text-red-700 dark:text-red-400 border-b border-red-500/30' : 'bg-blue-500/10 text-blue-700 dark:text-blue-400 border-b border-blue-500/30'}`}>
            {emailVerifyStatus === 'verifying' && <><Loader2 className="h-4 w-4 animate-spin" /> Verifying your email...</>}
            {emailVerifyStatus === 'success' && <>✅ Email verified successfully! Welcome aboard.</>}
            {emailVerifyStatus === 'error' && <>❌ Invalid or expired verification link. Please request a new one.</>}
          </div>
        )}

        {/* Messages Area */}
        <ScrollArea className="flex-1">
          <div className="p-3 sm:p-6 max-w-4xl mx-auto space-y-4">
            {messages.length === 0 && (() => {
              const agentInfo = AGENT_INFO[currentAgent] || AGENT_INFO['general'];
              const isGeneral = currentAgent === 'general';
              return (
                <>
                <Card className="p-5 sm:p-10 bg-muted/50 border-border" data-testid="welcome-card">
                  <div className="flex flex-col sm:flex-row sm:items-start gap-4 sm:gap-6">
                    {/* Icon */}
                    <div className="text-4xl sm:text-5xl flex-shrink-0 text-center sm:text-left">
                      {isGeneral && welcomeMessage.icon_image_url ? (
                        <img
                          src={`${API_BASE_URL}${welcomeMessage.icon_image_url}`}
                          alt="icon"
                          className="w-12 h-12 sm:w-14 sm:h-14 object-cover rounded-xl mx-auto sm:mx-0"
                        />
                      ) : (
                        agentInfo.icon
                      )}
                    </div>

                    {/* Content */}
                    <div className="flex-1 min-w-0">
                      <h3 className="text-xl sm:text-2xl font-bold text-foreground mb-1">
                        {isGeneral ? (welcomeMessage.title || agentInfo.title) : agentInfo.title}
                      </h3>
                      <p className="text-xs text-muted-foreground mb-3">
                        Welcome, <span className="text-primary font-medium">{user.username}</span>
                      </p>
                      <p className="text-sm sm:text-base text-muted-foreground leading-relaxed mb-4">
                        {isGeneral ? (welcomeMessage.description || agentInfo.description) : agentInfo.description}
                      </p>
                      <div className="flex flex-wrap gap-2 mt-2">
                        {agentInfo.capabilities.split(' · ').map((cap) => (
                          <span key={cap} className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary border border-primary/20">
                            {cap}
                          </span>
                        ))}
                      </div>
                      {!user.has_paid && user.role !== 'admin' && (
                        <div className="mt-4 p-3 bg-orange-500/10 border border-orange-500/20 rounded-lg">
                          <p className="text-sm text-orange-600 dark:text-orange-400">
                            🎉 You have {messagesRemaining !== null ? messagesRemaining : (5 - (user.messages_used || 0))} free messages remaining
                          </p>
                        </div>
                      )}
                    </div>
                  </div>
                </Card>

                {/* Quick prompt chips */}
                {agentInfo.quickPrompts && agentInfo.quickPrompts.length > 0 && (
                  <div className="mt-4">
                    <p className="text-xs text-muted-foreground mb-2 flex items-center gap-1">
                      <Zap className="h-3 w-3" /> Quick starts — tap to try:
                    </p>
                    <div className="flex flex-wrap gap-2">
                      {agentInfo.quickPrompts.map((prompt) => (
                        <button
                          key={prompt}
                          onClick={() => { setInputMessage(prompt); }}
                          className="px-3 py-1.5 rounded-full text-xs border border-primary/30 bg-primary/5 text-primary hover:bg-primary/15 transition-colors text-left"
                        >
                          {prompt}
                        </button>
                      ))}
                    </div>
                  </div>
                )}
                </>
              );
            })()}
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`flex ${msg.sender === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`w-fit max-w-[92%] sm:max-w-[80%] rounded-xl border p-3 sm:p-4 ${
                  msg.sender === 'user'
                    ? 'bg-blue-600 text-white border-blue-700'
                    : msg.isError
                      ? 'bg-red-50 dark:bg-red-950 border-red-200 dark:border-red-800'
                      : 'bg-card border-border shadow-sm'
                }`} data-testid={`message-${msg.sender}`}>
                  {msg.sender === 'user' ? (
                    <div className="whitespace-pre-wrap break-words text-sm sm:text-base">
                      {msg.message}
                    </div>
                  ) : (
                    <div className="ai-prose max-w-none overflow-x-auto">
                      <ReactMarkdown
                        remarkPlugins={[remarkGfm]}
                        components={{
                          code({ node, inline, className, children, ...props }) {
                            const match = /language-(\w+)/.exec(className || '');
                            return !inline && match ? (
                              <SyntaxHighlighter
                                style={vscDarkPlus}
                                language={match[1]}
                                PreTag="div"
                                className="rounded-md text-xs !overflow-x-auto"
                                {...props}
                              >
                                {String(children).replace(/\n$/, '')}
                              </SyntaxHighlighter>
                            ) : (
                              <code className="bg-muted text-blue-700 px-1 py-0.5 rounded text-xs font-mono break-all" {...props}>
                                {children}
                              </code>
                            );
                          }
                        }}
                      >
                        {msg.message}
                      </ReactMarkdown>
                    </div>
                  )}
                  <div className={`text-xs mt-2 ${
                    msg.sender === 'user' ? 'text-blue-100' : 'text-muted-foreground'
                  }`}>
                    {new Date(msg.timestamp).toLocaleTimeString()}
                  </div>
                  {msg.sender === 'ai' && !msg.isError && (
                    <button
                      onClick={() => copyMessage(msg.id, msg.message)}
                      className="mt-1 flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground transition-colors"
                      title="Copy response"
                    >
                      {copiedMessageId === msg.id
                        ? <><ClipboardCheck className="h-3 w-3 text-green-500" /> Copied</>
                        : <><Clipboard className="h-3 w-3" /> Copy</>
                      }
                    </button>
                  )}
                </div>
              </div>
            ))}

            {isLoading && (
              <div className="flex justify-start">
                <Card className="p-4 bg-card border-border" data-testid="loading-message">
                  <div className="flex items-center space-x-2">
                    <Loader2 className="h-4 w-4 animate-spin" />
                    <span className="text-muted-foreground">AI is thinking...</span>
                  </div>
                </Card>
              </div>
            )}
            
            <div ref={messagesEndRef} />
          </div>
        </ScrollArea>

        <Separator />

        {/* Input Area */}
        <div className="bg-card border-t border-border p-3 sm:p-6">
          <div className="max-w-4xl mx-auto">
            <div className="flex gap-2">
              <Input
                value={inputMessage}
                onChange={(e) => setInputMessage(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Ask for IT support, scripts, or troubleshooting help..."
                disabled={isLoading}
                className="flex-1 min-w-0"
                data-testid="message-input"
              />
              <Button
                onClick={sendMessage}
                disabled={isLoading || !inputMessage.trim()}
                data-testid="send-message-btn"
              >
                {isLoading ? (
                  <Loader2 className="h-4 w-4 animate-spin" />
                ) : (
                  <Send className="h-4 w-4" />
                )}
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Dialogs — called as functions, NOT as JSX components, to prevent
           unmount/remount on every parent re-render (e.g. while typing) */}
      {AdminPanel()}
      {PaymentDialog()}
      {PasswordChangeDialog()}
      {WelcomeEditDialog()}
      {LandingContentEditDialog()}
      {AdminSettingsDialog()}
    </div>
  );
}

export default App;